/**
 * Material library. Implements the frozen `ctx.materials` API.
 * owner: materials
 *
 *   ctx.materials.get(name, opts?)      cached & shared — never mutate the result
 *   ctx.materials.make(name, opts?)     a fresh clone you may mutate
 *   ctx.materials.texture(name, opts?)  albedo texture, with `.maps = {albedo, orm, normal}`
 *   ctx.materials.names                 every registered material
 *   ctx.materials.blueNoise             shared 512² blue noise (RepeatWrapping, Nearest)
 *
 * Extras other teams may find useful (additive, nothing removed):
 *   ctx.materials.tileMetres(name)      how many metres one texture repeat covers
 *   ctx.materials.detailNormal          the shared micro-surface normal map
 *   ctx.materials.envMap                fallback PMREM environment (lighting may replace)
 *   ctx.materials.surfaceOf(name)       the ballistics surface enum for a material
 *   ctx.materials.stats()               texture budget accounting
 *
 * Every material sets `userData.surface`. Meshes inherit it unless overridden.
 *
 * METALNESS, and why every material used to report 1.0
 * ----------------------------------------------------
 * The injected shader takes metalness from the packed ORM map's blue channel, which the
 * surface generators author per texel. The `metalness` scalar on the THREE material is
 * therefore NOT a multiplier and never was one usefully — it is the value the surface
 * has when the injection is not running, which happens on any `.clone()` that does not
 * go through `make()` (THREE.Material.copy does not copy `onBeforeCompile`, and
 * `metalnessMap` is deliberately unbound because samplers are the scarce resource,
 * FINDINGS F6). Leaving it at 1.0 "because the map scales it" meant a wooden palm trunk
 * genuinely reported metalness 1, and rendered as chrome as soon as `Env.js` cloned it
 * for a reflection probe. Two changes close it: the scalar is now authored physically
 * per material, and `applySurfaceShading` installs an instance `clone()` that carries
 * the injection across, so probe clones keep their maps as well as their metalness.
 *
 *   dielectric (metalness 0)  every concrete, plaster, stucco, brick, asphalt, sand,
 *                             gravel, dirt, wood, plywood, fabric, canvas, webbing,
 *                             sandbag, glass, rubber, plastic, tile, foliage, skin,
 *                             and every gun_polymer* / gun_rubber
 *   mostly dielectric         metal_painted 0.15 (paint over steel, chips are bare),
 *                             metal_rusted 0.10 (iron oxide is a dielectric)
 *   bare metal                metal_grate 0.85, corrugated_metal 0.70,
 *                             steel_brushed 1.0, metal_bare 1.0, gun_metal 1.0,
 *                             gun_alloy 1.0, gun_steel_bare 1.0, gun_optic 1.0
 *
 * SPECULAR, and why this library had none (FINDINGS F13)
 * ------------------------------------------------------
 * 60 of the 69 materials in `level_square` sat at roughness >= 0.9 and the only sub-0.3
 * surfaces anywhere in the scene were the render team's staging props, so the build's
 * brightest possible pixel was a cloud. Three sources of real specular are now in the
 * library, and all three are content rather than exposure:
 *
 *   bare metal       `metal_bare` (new) and the polish band in the `brushed` family,
 *                    which take roughness to 0.06-0.16 — the range at which a surface
 *                    returns an IMAGE of the sky rather than an average of it.
 *   burnished wear   `metal_painted` (183 uses in the map via itself and the 'metal'
 *                    alias) now rubs through to polished steel at its chip cores and
 *                    along its arris, at roughness ~0.11.
 *   wet ground       a runtime `wet` shading term, on by default at low coverage for
 *                    `asphalt` and available to any caller as
 *                    `ctx.materials.get(name, { wet: 0..1 })`. It costs no sampler and
 *                    no texture memory.
 *
 * Per-material IBL weight is exposed as the `ibl` shading option, NOT as
 * `material.envMapIntensity` — three.js overwrites that with `scene.environmentIntensity`
 * for any material whose own envMap is null, which is all of ours. See the
 * aomap_fragment patch in shaders/runtime.glsl.js.
 */

import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';
import { TextureFactory } from './TextureFactory.js';
import { applySurfaceShading, getSurfaceShading } from './shaders/runtime.glsl.js';
import { reRegisterMaterial } from './lightshaders/inject.js';

/* ===================================================================== *
 * Material definitions
 *
 *  family        which GLSL surface program synthesises the maps
 *  surface       ballistics surface enum (CONTRACTS.md)
 *  res           texture resolution tier (2048 hero / 1024 common / 512 background)
 *  tile          metres covered by one texture repeat
 *  colors        sRGB hex, converted to linear before reaching the shader
 *  params        uP[0..5]
 *  ns/hs/aos     normal strength / height scale / AO strength for the resolve pass
 *  metal         PHYSICAL metalness of the surface. 0 for every dielectric — paint,
 *                rust, plastic, cloth, wood, stone, skin, glass. 1 only for bare
 *                metal. This is authored, not a multiplier: the shader takes
 *                metalness from the ORM map, and this scalar is what a raw
 *                `.clone()` falls back to if the injection is ever lost.
 *  mat           MeshStandard/Physical constructor overrides
 *  sh            runtime shading options (detail / hex / pom / macro / triplanar)
 *
 * SAMPLING BUDGET. Every generator frequency below is bounded so the highest octave
 * still gets >= ~4 texels per cycle at the material's own `res`. Past Nyquist a
 * feature does not become "fine detail", it becomes noise that the mip chain then
 * averages to a flat tone — which is how a 380-cycle brush grain at 512 turned
 * `steel_brushed` into chrome and a 900-cycle one at 2048 turned `gun_metal` into mud.
 * ===================================================================== */

const DEFS = {

  /* ---------------- concrete family ---------------- */
  concrete_floor: {
    family: 'concrete', surface: 'concrete', res: 2048, tile: 2.4, seed: 11,
    colors: [0x59564e, 0x8d8779, 0x23201c],
    params: [
      [0.72, 0.62, 0.55, 0.0],   // aggregate, cracks, stains, paint
      [0.88, 1.5, 0.0, 0.18],    // rough, roughVar, formTies, spall
      [3.0, 1.7, 0.0, 0.80],     // macroFreq, tintVar, boardSeams, wear
      [0.34, 1.0, 0.0, 0.16],    // bubbles, grit, stucco, edgeChip
      [0.0, 0.90, 0, 0],         // paint peel, DRIFT (dust / damp / polish)
      /* uP[5].x = fine speckle (0.5-2 cm band). 1.35 -> 0.90.
       * This material is the plaza deck AND, via the level's slabs, the interior ceiling
       * and floor — the two surfaces a player spends the most time inside. It carried the
       * highest speckle weight in the set and it is the frame the reviewer called
       * "television static". The band is still the strongest in the library; it is no
       * longer strong enough to put a near-binary bright fleck on every centimetre. */
      [0.90, 0, 0, 0],
    ],
    ns: 1.30, hs: 0.055, aos: 1.0, hc: 2.8, metal: 0,
    sh: {
      detailTile: 22, detailStrength: 0.80, detailRough: 0.26, detailAlbedo: 0.86,
      hex: true, hexNear: 1.2, hexFar: 90, macro: true, triplanar: true,
      macroScale: 0.030, macroAlbedo: 0.34, macroRough: 0.16, macroHue: 0.24,
      octave: true, octaveAlbedo: 0.38, octaveRough: 0.14,
      /* A TRAFFIC PATH. This is the plaza deck and the interior floor — the two
       * surfaces the player spends the most time on — and a floor that has been walked
       * on for thirty years is not one gloss level. Feet polish the proud aggregate and
       * leave the joints and the low ground matt, so the polish is delivered by the
       * edge-wear layer (proud + open + on a slope) rather than by a noise field, and
       * the noise field on top of it is the slow "this half of the square is scuffed
       * and that half is dusty" variation. 2.6 m is a bit under a paving bay. */
      roughField: 0.17, roughFieldMetres: 2.6, roughFieldAlbedo: -0.05,
      edgeWear: 0.55, edgeCurve: 0.012, edgeRough: 0.46, edgeMetal: 0,
      edgeRelief: 1.0, edgeDatum: 0.930, edgeColor: 0x8d8779,
      edgeBreakMetres: 1.6, edgeGeoFade: 5.0,
    },
  },
  concrete_wall: {
    family: 'concrete', surface: 'concrete', res: 1024, tile: 2.6, seed: 23,
    colors: [0x655f55, 0x847d70, 0x28241e],
    params: [
      [0.46, 0.55, 0.72, 0.0],
      [0.84, 1.4, 0.9, 0.10],
      [2.6, 1.6, 0.50, 0.45],
      [0.14, 0.9, 0.0, 0.16],
      [0.0, 0.55, 0, 0],
      [0.75, 0, 0, 0],
    ],
    ns: 1.20, hs: 0.050, aos: 0.90, hc: 2.4,
    /* THE SELF-DETAIL LAYER MUST NOT RE-TILE A MAP THAT HAS METRE-SCALE STRUCTURE.
     *
     * That rule was already written in this file for planks, brick courses and weave —
     * and `detailAlbedo: 0` on each of them — but the concrete family was assumed
     * isotropic, and it is not: this material carries board seams (uP[2].z = 0.50) and
     * form-tie holes (uP[1].z = 0.9). Re-tiled at 20x and rotated 55 degrees, those
     * seams come back as a comb of diagonal lines with a period of tile/detailTile =
     * 2.6/20 = 13 cm, which at the closeup framing is 120 px.
     *
     * That is EXACTLY the critique's "regular diagonal lattice of dark hairlines at
     * ~120 px spacing at (1000,400), (1300,600), (1500,300)". Proved by ablation, not
     * assumed: with the hex tiling disabled the lattice is untouched; with only the
     * detail layer disabled it disappears completely
     * (shots/ablate/mat_closeup_concrete__{B_noHex,C_noDetail}.png).
     *
     * Strength comes down to where the stamped comb is below the aggregate's own
     * contrast, and the tile goes up so what survives is grain rather than structure.
     * The close-up interest the layer was providing is picked up by the octave and by
     * POM, which are both derived from the surface rather than copied from it. */
    sh: {
      detailTile: 34, detailStrength: 0.24, detailRough: 0.14, detailAlbedo: 0.10,
      hex: true, hexNear: 1.3, hexFar: 90, macro: true, macroScale: 0.05, macroAlbedo: 0.28,
      pom: 0.024, pomLayers: 40, pomFade: 11,
      octave: true, octaveAlbedo: 0.34, octaveRough: 0.14,
      facade: true, streak: 0.30,
      /* Knocked arrises. Every reveal, every corner, every window frame in this build
       * is factory-fresh (FINDINGS F20: "img_04's window frames at (620,500) are
       * factory-fresh, unbevelled, and catch no highlight along their edges"). A cast
       * concrete arris chips within a year and the chip shows the pale aggregate under
       * the laitance — lighter, rougher, dusty, never glossier. */
      roughField: 0.12, roughFieldMetres: 3.4, roughFieldAlbedo: 0.04,
      edgeWear: 0.62, edgeCurve: 0.008, edgeRough: 0.94, edgeMetal: 0,
      edgeRelief: 0.9, edgeDatum: 0.932, edgeColor: 0x9c9384,
      edgeBreakMetres: 0.9, edgeGeoFade: 9.0,
    },
  },
  concrete_wall_painted: {
    family: 'concrete', surface: 'concrete', res: 1024, tile: 2.6, seed: 37,
    colors: [0x5b5750, 0x6f6a62, 0x241f19, 0x79725f],
    params: [
      [0.32, 0.58, 0.85, 1.0],
      [0.80, 1.2, 0.7, 0.14],
      [2.4, 1.5, 0.7, 0.40],
      [0.16, 0.8, 0.0, 0.14],
      [0.90, 0.55, 0, 0],        // uP[4].x = paint peel, .y = drift
      [0.55, 0, 0, 0],
    ],
    ns: 1.05, hs: 0.046, aos: 0.85, hc: 2.4,
    // Same seam comb as concrete_wall, and this one carries MORE seam (0.7) — see the
    // ablation note there.
    sh: {
      detailTile: 34, detailStrength: 0.24, detailRough: 0.13, detailAlbedo: 0.10,
      hex: true, hexNear: 1.3, hexFar: 90, macro: true,
      pom: 0.016, pomFade: 9,
      octave: true, octaveAlbedo: 0.26, octaveRough: 0.10,
      facade: true, streak: 0.34,
      // Paint rubs THROUGH on an arris — to the grey render underneath, not to a
      // lighter version of the paint, so the worn colour is a different colour.
      roughField: 0.14, roughFieldMetres: 3.0, roughFieldAlbedo: 0.05,
      edgeWear: 0.75, edgeCurve: 0.007, edgeRough: 0.92, edgeMetal: 0,
      edgeRelief: 0.9, edgeDatum: 0.930, edgeColor: 0x8a8073,
      edgeBreakMetres: 0.8, edgeGeoFade: 9.0,
    },
  },
  plaster_wall: {
    family: 'concrete', surface: 'concrete', res: 1024, tile: 2.2, seed: 53,
    // Sunlit plaster at 0x8b8478 clipped to paper-white on every south face.
    // A real lime render sits nearer 40 % reflectance, and the dirt has to be in
    // the map, not left to the lighting.
    colors: [0x7c7568, 0x8d8578, 0x2a241c, 0x8b8478],
    params: [
      [0.10, 0.66, 0.85, 1.0],
      [0.78, 1.3, 0.0, 0.22],
      [2.8, 1.6, 0.0, 0.40],
      [0.35, 0.8, 0.55, 0.80],
      [0.7, 0.6, 0, 0],
      [0.60, 0, 0, 0],
    ],
    ns: 1.10, hs: 0.050, aos: 1.0,
    sh: {
      detailTile: 16, detailStrength: 0.70, detailRough: 0.14, detailAlbedo: 0.40,
      hex: true, hexNear: 1.3, hexFar: 90, macro: true, macroScale: 0.05, macroAlbedo: 0.24,
      pom: 0.014, pomFade: 9,
      octave: true, octaveAlbedo: 0.28, octaveRough: 0.10,
      facade: true, streak: 0.38,
      /* Lime render is soft: it loses its arris faster than anything else on the
       * street, and where it has gone the coarse brown backing coat shows. This is the
       * "plaster walls in img_04 (200-600, 200-700) are one roughness value" note. */
      roughField: 0.20, roughFieldMetres: 2.4, roughFieldAlbedo: 0.06,
      edgeWear: 0.85, edgeCurve: 0.009, edgeRough: 0.97, edgeMetal: 0,
      edgeRelief: 1.0, edgeDatum: 0.926, edgeColor: 0x8f826b,
      edgeBreakMetres: 0.7, edgeGeoFade: 10.0,
    },
  },
  stucco: {
    family: 'concrete', surface: 'concrete', res: 1024, tile: 2.0, seed: 71,
    colors: [0x827a6c, 0x968d7c, 0x2f2820],
    params: [
      [0.14, 0.45, 0.72, 0.0],
      [0.88, 1.2, 0.0, 0.12],
      [3.0, 1.5, 0.0, 0.45],
      [0.3, 1.0, 1.0, 0.45],
      [0.0, 0.5, 0, 0],
      [0.60, 0, 0, 0],
    ],
    ns: 1.30, hs: 0.065, aos: 1.1,
    sh: {
      detailTile: 18, detailStrength: 0.85, detailRough: 0.16, detailAlbedo: 0.45,
      hex: true, hexNear: 1.3, hexFar: 90, macro: true, macroScale: 0.05, macroAlbedo: 0.26,
      pom: 0.018, pomFade: 9,
      octave: true, octaveAlbedo: 0.28, octaveRough: 0.10,
      facade: true, streak: 0.36,
      roughField: 0.18, roughFieldMetres: 2.2, roughFieldAlbedo: 0.05,
      edgeWear: 0.80, edgeCurve: 0.010, edgeRough: 0.98, edgeMetal: 0,
      edgeRelief: 1.0, edgeDatum: 0.924, edgeColor: 0x968a74,
      edgeBreakMetres: 0.7, edgeGeoFade: 10.0,
    },
  },
  /**
   * Retired from the level palette because "a vertex tint multiplies channels, so it
   * provably cannot desaturate a saturated red". That is correct, and it is why the
   * fix has to be here.
   *
   *  proportion  WAS 4 x 16 over a 2.4 m tile = 60 x 15 cm bricks. A brick is 21.5 x
   *              6.5 cm. NOW 10 x 30 = 24 x 8 cm including a 9 mm joint.
   *  colour      WAS 0x7a3d2d, HSV saturation 0.63 — brand-new engineering brick.
   *              NOW 0x93796a / 0x6d5a4d, saturation 0.28 / 0.29: sun-bleached dusty
   *              clay, with the per-brick desaturation range in the generator widened
   *              from a fixed 0.18 to 0.10-0.52 so no two bricks share a chroma.
   *  mortar      WAS one wobble amplitude for every joint. NOW a second, lower
   *              frequency wander plus a per-course rake depth, so the bed joints are
   *              struck unevenly the way a hand-laid wall is.
   */
  brick_wall: {
    family: 'brick', surface: 'concrete', res: 1024, tile: 2.4, seed: 97,
    colors: [0x93796a, 0x6d5a4d, 0x8d887c, 0x272219],
    params: [
      [10.0, 30.0, 0.11, 0.8],   // cols, rows, mortar width, chip
      [0.55, 0.35, 0.22, 0.0],   // grime, efflorescence, moss
    ],
    ns: 1.35, hs: 0.10, aos: 1.2, hc: 3.2, metal: 0,
    /* "the brick wall repeats with a period of ~340 px, visible at x~1250 and x~1590."
     * A 2.4 m tile across a 40 m corridor repeats 16.7 times, and the only things
     * fighting that period were a 0.22 macro term and a 0.22 octave. Both are the right
     * tools — an octave at 0.2831 scale beats against the tile at 8.5 m, and the macro
     * field is world-space so it never repeats at all — they were simply too quiet to
     * cover a 16x repeat. Raised, and the macro moved to a longer world period so the
     * two beat frequencies do not agree with each other either. Hex tiling is still off
     * on purpose: stochastic offsets would break the bond pattern, and a brick wall
     * whose courses do not line up is a worse tell than a findable tile. */
    sh: {
      detailTile: 20, detailStrength: 0.45, detailRough: 0.14, detailAlbedo: 0,
      /* First pass at macroAlbedo 0.36 / octaveAlbedo 0.38 did break the period, and it
       * also turned the corridor wall sooty — large dark blotches that read as fire
       * damage rather than as weathering. Backed off to where the beat is still there
       * and the wall is still brick. */
      hex: false, macro: true, macroScale: 0.055, macroAlbedo: 0.26, macroRough: 0.09,
      macroHue: 0.14,
      pom: 0.030, pomLayers: 48, pomFade: 13,
      octave: true, octaveAlbedo: 0.28, octaveRough: 0.10,
      facade: true, streak: 0.30, jitterHue: 0.10,
      /* A brick arris is the most reliably knocked edge in any street: the frog and
       * the corners spall, and what shows is the paler unfired-looking core. The
       * relief term does the per-brick work — the height field already knows where
       * every arris is — and the geometric term catches the reveals. */
      roughField: 0.15, roughFieldMetres: 2.8, roughFieldAlbedo: 0.05,
      edgeWear: 0.70, edgeCurve: 0.006, edgeRough: 0.99, edgeMetal: 0,
      edgeRelief: 1.0, edgeDatum: 0.928, edgeColor: 0xa08c7c,
      edgeBreakMetres: 0.5, edgeGeoFade: 9.0,
    },
  },

  /* ---------------- ground ---------------- */
  asphalt: {
    family: 'asphalt', surface: 'concrete', res: 1024, tile: 3.0, seed: 131,
    colors: [0x27272a, 0x827e76, 0x968f82],
    params: [[0.80, 0.65, 0.45, 0.5]],
    ns: 1.35, hs: 0.058, aos: 1.15,
    sh: {
      detailTile: 26, detailStrength: 0.75, detailRough: 0.20, detailAlbedo: 0.60,
      hex: true, hexNear: 1.2, hexFar: 90, macro: true, triplanar: true,
      macroScale: 0.04, macroAlbedo: 0.30, macroRough: 0.14,
      octave: true, octaveAlbedo: 0.30, octaveRough: 0.12,
      /* Damp patches. Asphalt is the one ground surface in the map that genuinely holds
       * water — it is impermeable, it is laid to a crown, and the low side of every
       * camber stays dark for hours. Small and sparse on purpose (threshold 0.66): the
       * point is a handful of real sub-0.15 highlights in a frame whose brightest
       * possible pixel is otherwise a cloud, not to make the souk look rained on.
       * Any caller can dial it: ctx.materials.get('asphalt', { wet: 0.9 }). */
      wet: 0.55, wetMetres: 9.0, wetThreshold: 0.66, wetRough: 0.075,
      /* Tyre-polished aggregate in the wheel tracks against loose, dusty binder at the
       * kerb line. This is the single biggest readability gain per line in the library:
       * a road that is one roughness value reads as a texture, and a road with a polish
       * path reads as a road. */
      roughField: 0.19, roughFieldMetres: 4.5, roughFieldAlbedo: -0.06,
      edgeWear: 0.45, edgeCurve: 0.010, edgeRough: 0.52, edgeMetal: 0,
      edgeRelief: 1.0, edgeDatum: 0.934, edgeColor: 0x6e6a63,
      edgeBreakMetres: 2.2, edgeGeoFade: 5.0,
    },
  },
  sand: {
    family: 'granular', surface: 'sand', res: 1024, tile: 3.0, seed: 149,
    colors: [0xa8906a, 0x8b7857, 0x6f5d41],
    params: [[0.12, 1.0, 0.15, 0.0]],
    ns: 1.05, hs: 0.06, aos: 1.0,
    sh: {
      detailTile: 30, detailStrength: 0.80, detailRough: 0.16, detailAlbedo: 0.40,
      hex: true, hexNear: 1.2, hexFar: 90, macro: true, triplanar: true, macroScale: 0.05,
      octave: true, octaveAlbedo: 0.24, octaveRough: 0.08,
    },
  },
  gravel: {
    family: 'granular', surface: 'dirt', res: 1024, tile: 2.2, seed: 163,
    colors: [0x55514a, 0x827c71, 0x322e28],
    params: [[1.0, 0.0, 0.35, 0.15]],
    ns: 1.6, hs: 0.115, aos: 1.45, hc: 3.0,
    sh: {
      detailTile: 24, detailStrength: 0.65, detailRough: 0.16, detailAlbedo: 0.45,
      hex: true, hexNear: 1.2, hexFar: 90, macro: true, macroScale: 0.05, macroAlbedo: 0.24,
      pom: 0.052, pomLayers: 48, pomFade: 12,
      octave: true, octaveAlbedo: 0.28, octaveRough: 0.10,
    },
  },
  dirt: {
    family: 'granular', surface: 'dirt', res: 1024, tile: 2.8, seed: 179,
    colors: [0x46392a, 0x5c4a34, 0x1f1810],
    params: [[0.30, 0.10, 0.55, 0.30]],
    ns: 1.30, hs: 0.078, aos: 1.2,
    sh: {
      detailTile: 26, detailStrength: 0.70, detailRough: 0.16, detailAlbedo: 0.50,
      hex: true, hexNear: 1.2, hexFar: 90, macro: true, triplanar: true, macroScale: 0.05, macroAlbedo: 0.28,
      octave: true, octaveAlbedo: 0.28, octaveRough: 0.10,
    },
  },

  /* ---------------- metal ---------------- */
  /* Painted steel: the coat is a DIELECTRIC. Only the chipped-through spots and the
   * bare weld beads are metal, and the map says so — the scalar is the whole-surface
   * average a raw clone should fall back to. */
  metal_painted: {
    family: 'metal_painted', surface: 'metal', res: 1024, tile: 1.6, seed: 211,
    colors: [0x36402e, 0x633518, 0x743e22, 0x1d1b18],
    params: [
      [0.42, 0.34, 0.0, 0.5],    // chip, rust, weld, dent
      [0.45, 0.5, 0.6, 0.5],     // scratches, gloss, panels, grime
      [0.80, 0, 0, 0],           // burnish: rubbed-through steel at roughness ~0.11
    ],
    ns: 1.1, hs: 0.045, aos: 1.0, metal: 0.15,
    sh: { detailTile: 20, detailStrength: 0.7, detailRough: 0.12, hex: true, macro: true, ibl: 1.15,
      /* THE ONE PLACE IN THE BUILD WHERE EDGE WEAR ALREADY WORKED was the rusted barrel
       * rim, which the critic called the best-looking object in eight of our frames.
       * This is the same mechanism made general: paint over steel does not fade on an
       * arris, it comes OFF, and what is underneath is metal — so the worn state is
       * `edgeMetal: 1` at a low roughness, not a lighter paint. That metalness step is
       * the whole reason the effect reads as damage rather than as a highlight.
       *
       * `metal_painted` is 183 uses in the map (itself plus the 'metal' alias): every
       * railing, shutter, kiosk frame, bollard and door in the street. */
      roughField: 0.16, roughFieldMetres: 1.6, roughFieldAlbedo: 0.0,
      edgeWear: 0.95, edgeCurve: 0.004, edgeRough: 0.22, edgeMetal: 1.0,
      edgeRelief: 1.0, edgeDatum: 0.922, edgeColor: 0x9a958d,
      edgeBreakMetres: 0.35, edgeGeoFade: 8.0 },
  },
  /* Rust is iron oxide — a dielectric. At 0.86 coverage almost nothing here is metal. */
  metal_rusted: {
    family: 'metal_rusted', surface: 'metal', res: 1024, tile: 1.6, seed: 223,
    colors: [0x3a2313, 0x6a3b1c, 0x746040],
    params: [[0.86, 0.45, 0.6, 0.5]],
    ns: 0.95, hs: 0.032, aos: 1.0, hc: 2.4, metal: 0.10,
    sh: { detailTile: 22, detailStrength: 0.45, detailRough: 0.14, hex: true, macro: true, macroScale: 0.12, macroAlbedo: 0.22, pom: 0.010,
      // The reference case. A rusted rim polishes back to metal wherever it is
      // handled or rubbed — the scale flakes off the high points first.
      roughField: 0.18, roughFieldMetres: 1.2, roughFieldAlbedo: 0.0,
      edgeWear: 0.80, edgeCurve: 0.005, edgeRough: 0.30, edgeMetal: 0.85,
      edgeRelief: 1.0, edgeDatum: 0.920, edgeColor: 0x8e857a,
      edgeBreakMetres: 0.30, edgeGeoFade: 8.0 },
  },
  /* Galvanised steel bar grating. Was reading as a white pattern painted on a ball:
   * `tfbm2(uv, vec2(200,50), 4)` at 512 runs its top octave at 1600 cycles, 0.3 texels
   * each, so the mill texture averaged to a flat tone and only the alpha lattice
   * survived. Frequency brought inside the map, res doubled, base roughness dropped so
   * the bars carry a real metal highlight, and the zinc value darkened off white. */
  metal_grate: {
    family: 'grate', surface: 'metal', res: 1024, tile: 1.0, seed: 239,
    colors: [0x5f646a, 0x653519],
    params: [[8.0, 8.0, 0.20, 0.55]],
    ns: 1.6, hs: 0.10, aos: 1.3, metal: 0.85,
    mat: { transparent: false, alphaTest: 0.5, side: THREE.DoubleSide },
    sh: { detailTile: 14, detailStrength: 0.5, detailRough: 0.1, hex: false, macro: false },
  },
  /* Same story: the sheet read as stripes rather than metal. Darker galvanised value,
   * lower base roughness, spangle strengthened, and the mill noise brought inside the
   * map's Nyquist so it survives instead of averaging away. */
  corrugated_metal: {
    family: 'corrugated', surface: 'metal', res: 1024, tile: 2.0, seed: 251,
    colors: [0x70757b, 0x6b3a1c, 0x475343],
    params: [[9.0, 0.55, 0.62, 0.45]],
    ns: 1.4, hs: 0.11, aos: 1.2, metal: 0.70,
    sh: { detailTile: 20, detailStrength: 0.7, detailRough: 0.12, hex: false, macro: true },
  },
  /**
   * "Reads as chrome, not brushed. There is no anisotropic streak."
   *
   * There was no streak because there was no signal. brushF 380 on a 512 map is 1.35
   * texels per cycle, and `tfbm2(..., 4 octaves)` takes the top octave to 3040 cycles
   * — 0.17 texels. Everything above Nyquist folded into per-texel noise and the mip
   * chain then flattened it to a single grey, i.e. chrome. NOW 1024 with brushF 80:
   * 12.8 texels on the fundamental and 3.2 on the top octave, so the grooves are real
   * geometry in the normal map. The roughness spread along the grain is widened from
   * 0.24 to 0.44 and the base dropped from 0.52 to 0.30, which is what turns those
   * grooves into a stretched highlight instead of a texture.
   */
  steel_brushed: {
    family: 'brushed', surface: 'metal', res: 1024, tile: 1.0, seed: 269,
    /* "`steel_brushed` and `metal_bare` are indistinguishable." They were: 0x9aa0a8 vs
     * 0x8f959c, brushF 80 vs 55, polish 0.55 vs 0.68. Two names for one satin nickel.
     * They are now separated by the thing that actually separates them in the hand —
     * this one is a FINISH (a directional abrasive on stainless: tight grain, high
     * anisotropy, hardly any exposed bare metal), `metal_bare` is a CONDITION (hot-rolled
     * structural steel that has been outdoors: mill scale, pitting, worn arris). */
    colors: [0x9ea6b0, 0x303336],
    params: [
      [96.0, 0.20, 0.62, 0.20],   // brush freq, rough base, aniso spread, wear
      [0.0, 0.55, 0.16, 0.14],    // bluing, machining, oil, pits
      [0.30, 0.55, 0.85, 0.0],    // polish (bare-metal band), smudge, finish zoning
    ],
    ns: 1.35, hs: 0.030, aos: 0.8, metal: 1.0,
    physical: true,
    /* A scalar roughness cannot stretch a highlight. `steel_brushed` "reads as chrome,
     * not brushed" because a round GGX lobe on a fine grain IS chrome — the grain only
     * becomes a brush mark when the lobe itself is elongated along it. three.js has an
     * anisotropic GGX in MeshPhysicalMaterial and it costs no sampler when driven by
     * the scalar; this is the one place in the library that genuinely needs it. */
    mat: { anisotropy: 0.78, anisotropyRotation: Math.PI * 0.5 },
    sh: { detailTile: 16, detailStrength: 0.35, detailRough: 0.10, hex: false, macro: false,
          specAA: true, ibl: 1.35 },
  },
  /**
   * Bare, unpainted structural steel — handrails, bollards, pipe runs, kerb angle,
   * lamp columns, the exposed rebar in the rubble.
   *
   * FINDINGS F13 named the missing ingredient exactly: "no window returns a sun
   * reflection, no surface is wet, no metal is bare". 60 of 69 materials in
   * `level_square` are at roughness >= 0.9 and the only sub-0.3 materials anywhere in
   * the scene were the render team's staging props. Below ~0.20 a metal stops averaging
   * the sky and starts returning an image of it, and that is the only mechanism by
   * which a frame with no emitters and no sun disc gets a pixel near 1.0.
   */
  metal_bare: {
    family: 'brushed', surface: 'metal', res: 512, tile: 0.9, seed: 271,
    /* Hot-rolled structural steel that has stood outside, not a plated fitting. The
     * measured p99 0.988 at saturation 0.109 with no macro variation is a chrome ball;
     * what makes this steel is that the polished band is a MINORITY of the surface and
     * the rest is scale, pitting and tonal patchiness. Pits and edge wear both up, the
     * polished fraction down from two thirds to a third, and the finish zoning — which
     * the generator now applies at three times the amplitude over two scales — taken
     * near full. Roughness base is up 0.13 -> 0.19; the sub-0.20 mirror band that
     * FINDINGS F13 needs somewhere in the build is `gun_steel_bare`'s job, and it keeps
     * it. */
    colors: [0x8c9198, 0x2b2e31],
    params: [
      [55.0, 0.19, 0.42, 0.55],
      [0.0, 0.30, 0.10, 0.48],
      [0.34, 0.45, 0.92, 0.0],
    ],
    ns: 1.20, hs: 0.026, aos: 0.85, metal: 1.0,
    physical: true,
    mat: { anisotropy: 0.42, anisotropyRotation: Math.PI * 0.5 },
    sh: { detailTile: 14, detailStrength: 0.35, detailRough: 0.10, hex: false, macro: true,
          macroScale: 0.20, macroAlbedo: 0.10, macroRough: 0.08, specAA: true, ibl: 1.45 },
  },

  /* ---------------- wood ---------------- */
  wood_plank: {
    family: 'wood', surface: 'wood', res: 1024, tile: 2.4, seed: 307,
    colors: [0x4f3a23, 0x1d1207, 0x6a7468, 0x1b170f],
    params: [
      [5.0, 9.0, 0.7, 0.22],     // planks, ring freq, knots, varnish
      [0.0, 0.7, 0.0, 0.10],     // paint, wear, plywood, gap depth
      [0.6, 0, 0, 0],            // uP[2].x = nails
    ],
    ns: 1.25, hs: 0.055, aos: 1.15, hc: 3.4,
    // detailAlbedo stays 0 on every directional map (planks, courses, weave): the
    // self-detail layer re-tiles the material's OWN normal map, and re-tiling a map
    // that has metre-scale structure at 22x paints a comb of that structure back over
    // the albedo. On isotropic grain (concrete, asphalt, dirt) it is free detail; on a
    // plank wall it is a curtain of vertical streaks.
    sh: { detailTile: 27, detailStrength: 0.13, detailRough: 0.18, detailAlbedo: 0, hex: false, macro: true, macroScale: 0.11, macroAlbedo: 0.20, pom: 0.010, pomLayers: 40, pomFade: 7,
      // Timber does not chip, it burnishes: the arris rounds and goes SMOOTHER and
      // slightly darker with handling, which is the opposite direction from plaster.
      roughField: 0.16, roughFieldMetres: 1.4, roughFieldAlbedo: -0.05,
      edgeWear: 0.60, edgeCurve: 0.006, edgeRough: 0.42, edgeMetal: 0,
      edgeRelief: 0.8, edgeDatum: 0.930, edgeColor: 0x6a5334,
      edgeBreakMetres: 0.45, edgeGeoFade: 7.0 },
  },
  wood_crate: {
    family: 'wood', surface: 'wood', res: 512, tile: 1.1, seed: 311,
    colors: [0x66502c, 0x30200e, 0x6a7468, 0x1e1a13],
    params: [
      [4.0, 8.0, 0.6, 0.1],
      [0.0, 0.55, 0.0, 0.09],
      [0.45, 0, 0, 0],
    ],
    ns: 1.2, hs: 0.05, aos: 1.1, hc: 3.4,
    sh: { detailTile: 23, detailStrength: 0.16, detailRough: 0.19, detailAlbedo: 0, hex: false, macro: true, pom: 0.012,
      roughField: 0.16, roughFieldMetres: 0.9, roughFieldAlbedo: -0.05,
      edgeWear: 0.80, edgeCurve: 0.005, edgeRough: 0.40, edgeMetal: 0,
      edgeRelief: 0.9, edgeDatum: 0.926, edgeColor: 0x8a7048,
      edgeBreakMetres: 0.30, edgeGeoFade: 6.0 },
  },
  wood_painted: {
    family: 'wood', surface: 'wood', res: 512, tile: 1.8, seed: 313,
    colors: [0x5b4527, 0x2b1c0b, 0x59665b, 0x221d16],
    params: [
      [4.0, 9.0, 0.55, 0.12],
      [0.55, 0.45, 0.0, 0.09],
      [0.35, 0, 0, 0],
    ],
    ns: 1.15, hs: 0.05, aos: 1.1, hc: 3.4,
    sh: { detailTile: 23, detailStrength: 0.16, detailRough: 0.18, detailAlbedo: 0, hex: false, macro: true, pom: 0.010,
      // Painted timber: the paint comes off the arris and the bare wood shows.
      roughField: 0.15, roughFieldMetres: 1.1, roughFieldAlbedo: 0.03,
      edgeWear: 0.85, edgeCurve: 0.005, edgeRough: 0.72, edgeMetal: 0,
      edgeRelief: 0.9, edgeDatum: 0.926, edgeColor: 0x6d5533,
      edgeBreakMetres: 0.30, edgeGeoFade: 6.0 },
  },
  plywood: {
    family: 'wood', surface: 'wood', res: 512, tile: 2.4, seed: 317,
    colors: [0x66512d, 0x392a13, 0x6a7468, 0x1e1a13],
    params: [
      [0.0, 16.0, 0.45, 0.05],
      [0.0, 0.5, 1.0, 0.0],
    ],
    ns: 1.0, hs: 0.035, aos: 0.9,
    sh: { detailTile: 25, detailStrength: 0.16, detailRough: 0.18, detailAlbedo: 0, hex: false, macro: true },
  },

  /* ---------------- fabric ---------------- */
  fabric_tarp: {
    family: 'fabric', surface: 'fabric', res: 1024, tile: 0.9, seed: 353,
    colors: [0x333f31, 0x272f24, 0x414a38, 0x1f1c17],
    params: [
      [150.0, 150.0, 0.34, 0.22], // weave freq, thickness, fuzz
      [0.0, 0.6, 0.0, 0.7],       // camo, dirt, ribs, coated
      [1.0, 0, 0, 0],             // uP[2].x = wrinkles
    ],
    ns: 1.15, hs: 0.045, aos: 1.2, metal: 0,
    mat: { side: THREE.DoubleSide },
    /* translucent is now divided by PI in the shader, so 0.69 here is the same amount
     * of transmitted light the old un-normalised 0.22 produced. What changes is that
     * it is now shadowed by the sun cascade like everything else. */
    sh: { detailTile: 14, detailStrength: 0.5, detailRough: 0.12, hex: false, macro: true,
          translucent: 0.62, transPower: 3.4, transWrap: 0.5, transTint: 0xa8a37a, transTintMix: 0.4 },
  },
  /**
   * The souk canopy — now the map's main light-shaping element, lit from above and
   * read from underneath, and it had no `translucent` term at all while `fabric_tarp`
   * did. A sunlit awning with no transmission is a black slab from below.
   * Weave also went from 140 threads over 0.45 m (3.2 mm, upholstery) to 210 (2.1 mm,
   * cotton duck) with the relief depth cut so it stops reading as a woven mat.
   */
  canvas: {
    family: 'fabric', surface: 'fabric', res: 1024, tile: 0.45, seed: 359,
    colors: [0x5b5240, 0x4c4536, 0x69604b, 0x221e16],
    params: [
      [210.0, 210.0, 0.26, 0.6],
      [0.0, 0.75, 0.0, 0.0],
      [0.85, 0, 0, 0],
    ],
    ns: 1.05, hs: 0.038, aos: 1.3, metal: 0,
    mat: { side: THREE.DoubleSide },
    sh: { detailTile: 12, detailStrength: 0.30, detailRough: 0.10, hex: false, macro: true,
          macroScale: 0.35, macroAlbedo: 0.24,
          translucent: 0.55, transPower: 3.0, transWrap: 0.58, transTint: 0xcbb489, transTintMix: 0.35 },
  },
  cloth_camo: {
    family: 'fabric', surface: 'fabric', res: 1024, tile: 0.55, seed: 367,
    colors: [0x555141, 0x414735, 0x6e6650, 0x282a21],
    params: [
      [160.0, 160.0, 0.34, 0.32],
      [1.0, 0.35, 0.0, 0.0],
      [0.7, 0, 0, 0],
    ],
    ns: 1.15, hs: 0.04, aos: 1.2,
    mat: { side: THREE.DoubleSide },
    sh: { detailTile: 12, detailStrength: 0.5, detailRough: 0.12, hex: false, macro: true },
  },
  webbing: {
    family: 'fabric', surface: 'fabric', res: 512, tile: 0.35, seed: 373,
    colors: [0x3d3d31, 0x32352b, 0x4a4a3b, 0x22221b],
    params: [
      [95.0, 50.0, 0.55, 0.15],
      [0.0, 0.3, 1.0, 0.15],
      [0.25, 0, 0, 0],
    ],
    ns: 1.4, hs: 0.055, aos: 1.3,
    mat: { side: THREE.DoubleSide },
    sh: { detailTile: 10, detailStrength: 0.5, detailRough: 0.12, hex: false, macro: false },
  },
  /**
   * "Reads as wicker basket weave rather than hessian." It did: 70 threads across a
   * 0.45 m tile is a 6.4 mm yarn, which is basketry, not sacking — and with the relief
   * at 0.42 and a 0.075 height scale each crossing was a chunky proud dome, and the
   * per-crossing tonal jitter (0.72 + 0.5) then painted a checkerboard on top of it.
   * Jute sacking yarn is ~2 mm. 190 threads over 0.45 m gives 2.4 mm at 5.4 texels
   * each, relief down to 0.24, height scale down to 0.048, tonal jitter down to a
   * quarter of its range — a woven cloth pulled tight over sand, not a hamper.
   */
  sandbag: {
    family: 'sandbag', surface: 'fabric', res: 1024, tile: 0.45, seed: 379,
    colors: [0x6d5c3c, 0x4e442b, 0x33291d],
    params: [[190.0, 190.0, 0.24, 0.78]],
    ns: 1.25, hs: 0.048, aos: 1.4, hc: 3.0, metal: 0,
    sh: { detailTile: 12, detailStrength: 0.5, detailRough: 0.16, hex: false, macro: true, pom: 0.012 },
  },

  /* ---------------- glass ---------------- */
  glass: {
    family: 'glass', surface: 'glass', res: 512, tile: 2.0, seed: 401,
    colors: [0xe8f0f0, 0xb8bcb4, 0x8f8f82],
    params: [[0.10, 0.15, 0.0, 0.15]],
    ns: 0.5, hs: 0.02, aos: 0.0,
    physical: true,
    mat: {
      transmission: 1.0, ior: 1.52, thickness: 0.012, roughness: 1.0, metalness: 0.0,
      transparent: false, opacity: 1.0, side: THREE.DoubleSide,
      specularIntensity: 1.0, envMapIntensity: 1.4, attenuationDistance: 2.5,
      attenuationColor: new THREE.Color(0xcfe0dd),
    },
    /* Every pane is authored with the same 0..1 UV span, so without a world-space
     * offset every pane in a façade carries the identical grime mottle and the wall
     * reads as one decal sheet repeated. 1.4 m cells put each pane on its own patch of
     * the map; the cell boundary lands on the mullion. */
    sh: { detailTile: 8, detailStrength: 0.15, detailRough: 0.05, hex: false, macro: true,
          macroScale: 0.22, macroAlbedo: 0.10, macroRough: 0.05, specAA: true,
          uvJitter: 0.85, uvJitterMetres: 1.4 },
  },
  glass_dirty: {
    family: 'glass', surface: 'glass', res: 512, tile: 2.0, seed: 409,
    colors: [0xdfe6e4, 0xa8a496, 0x7a7466],
    params: [[1.0, 0.9, 0.35, 0.95]],
    ns: 0.8, hs: 0.03, aos: 0.0,
    physical: true,
    mat: {
      transmission: 0.92, ior: 1.52, thickness: 0.012, roughness: 1.0, metalness: 0.0,
      transparent: false, opacity: 1.0, side: THREE.DoubleSide,
      specularIntensity: 1.0, envMapIntensity: 1.1, attenuationDistance: 1.2,
      attenuationColor: new THREE.Color(0xb9c2b4),
    },
    sh: { detailTile: 10, detailStrength: 0.3, detailRough: 0.08, hex: false, macro: true,
          macroScale: 0.22, macroAlbedo: 0.14, macroRough: 0.06,
          uvJitter: 0.85, uvJitterMetres: 1.4 },
  },

  /* ---------------- polymer / rubber / plastic ---------------- */
  rubber: {
    family: 'polymer', surface: 'metal', res: 512, tile: 0.8, seed: 431,
    colors: [0x171819],
    params: [
      [0.9, 130.0, 0.86, 0.0],
      [0.55, 0.35, 0.5, 0.0],
      [0.0, 1.0, 0, 0],           // soft-touch: vulcanised rubber has no gloss at all
    ],
    ns: 1.2, hs: 0.04, aos: 1.1,
    sh: { detailTile: 16, detailStrength: 0.6, detailRough: 0.12, hex: false, macro: false, ibl: 0.45 },
  },
  plastic_crate: {
    family: 'polymer', surface: 'metal', res: 512, tile: 1.0, seed: 433,
    colors: [0x1d3a5c],
    params: [
      [0.55, 70.0, 0.42, 0.0],
      [0.30, 0.6, 0.6, 0.7],
      [0.25, 0.0, 0, 0],
    ],
    ns: 1.1, hs: 0.045, aos: 1.0,
    sh: { detailTile: 14, detailStrength: 0.5, detailRough: 0.12, hex: false, macro: false },
  },
  /**
   * "A plain white grid. Reads as a modern bathroom, not a Levantine market town."
   *
   * The tile body was a two-colour lerp between 0x8b8478 and 0x777166 — two greys four
   * per cent apart — so every tile in the field was the same value and only the grout
   * had contrast. NOW an ochre-cream glaze with a per-tile accent (uC[2]) dropped on a
   * seeded fraction of the field, the way a hand-laid zellige floor mixes a batch in,
   * and per-tile value spread widened so the field breathes.
   */
  tile_ceramic: {
    family: 'tile', surface: 'concrete', res: 1024, tile: 1.6, seed: 439,
    colors: [0xa79878, 0x8e8065, 0x6d7f80, 0x8d857a],
    params: [
      [8.0, 8.0, 0.055, 0.7],    // cols, rows, grout width, chip
      [0.6, 0.26, 0.30, 0],      // dirt, accent-tile fraction, per-tile value spread
    ],
    ns: 1.15, hs: 0.06, aos: 1.2, metal: 0,
    sh: { detailTile: 16, detailStrength: 0.45, detailRough: 0.10, hex: false, macro: true, pom: 0.012,
      // A glazed tile floor is the clearest polish path there is: the glaze survives
      // in the traffic lane and is scuffed matt everywhere feet turn.
      roughField: 0.22, roughFieldMetres: 1.8, roughFieldAlbedo: -0.06,
      edgeWear: 0.55, edgeCurve: 0.004, edgeRough: 0.86, edgeMetal: 0,
      edgeRelief: 0.9, edgeDatum: 0.934, edgeColor: 0xb0a894,
      edgeBreakMetres: 0.55, edgeGeoFade: 6.0 },
  },
  /** Terracotta floor tile — the variant the level team asked for. */
  tile_terracotta: {
    family: 'tile', surface: 'concrete', res: 1024, tile: 1.2, seed: 443,
    colors: [0xa46f4e, 0x8a5a3f, 0x7d6a58, 0x8a8074],
    params: [
      [6.0, 6.0, 0.070, 0.9],
      [0.75, 0.18, 0.42, 0],
    ],
    ns: 1.25, hs: 0.075, aos: 1.3, metal: 0,
    sh: { detailTile: 16, detailStrength: 0.55, detailRough: 0.14, hex: false, macro: true,
          macroScale: 0.07, macroAlbedo: 0.20, pom: 0.016,
          octave: true, octaveAlbedo: 0.18, octaveRough: 0.08,
          roughField: 0.20, roughFieldMetres: 1.6, roughFieldAlbedo: -0.05,
          edgeWear: 0.70, edgeCurve: 0.004, edgeRough: 0.62, edgeMetal: 0,
          edgeRelief: 1.0, edgeDatum: 0.928, edgeColor: 0xb08268,
          edgeBreakMetres: 0.45, edgeGeoFade: 6.0 },
  },

  /* ---------------- organic ---------------- */
  /**
   * Dry Levantine roadside scrub in mid-afternoon sun.
   *
   * WAS: 0x3f5d24 / 0x263e17 — HSV saturation 0.61 and 0.63, a chroma green that
   * belongs on a watered English lawn, not on a dust-blown alley. Combined with a
   * translucency term that was ~6x over the frame's own light scale and tinted
   * 0x8fc24e (saturation 0.60), every tuft in `level_alley` read as a fluorescent
   * emitter. NOW: saturation 0.28 / 0.29, an olive-khaki with straw-brown dead
   * matter carrying most of the tuft, and the transmitted light is warm straw
   * rather than chroma green so a backlit blade glows the colour of dry grass.
   */
  foliage: {
    family: 'foliage', surface: 'foliage', res: 1024, tile: 0.7, seed: 457,
    colors: [0x6d7050, 0x474b34, 0x8d7c56],
    params: [[1.0, 0.54, 0.34, 0]],   // leaves, dryness, brown-blade fraction
    ns: 1.0, hs: 0.05, aos: 1.0, metal: 0,
    mat: {
      side: THREE.DoubleSide, transparent: false,
      /* alphaTest 0.42 against an alpha ramp that only reached 0.65 on a leaf centre
       * cut most of every blade away — hence "an alpha card with large holes, broken
       * alpha, not foliage". The generator now covers >50 % of the tile and the ramp
       * starts at 0.26, so the test has to come down with it or the extra coverage is
       * thrown away at the same threshold. */
      alphaTest: 0.30,
      roughness: 1.0, metalness: 0.0,
    },
    sh: { detailTile: 6, detailStrength: 0.35, detailRough: 0.08, hex: false, macro: false,
          translucent: 0.60, transPower: 2.2, transWrap: 0.60, transTint: 0xbb9a5e, transTintMix: 0.45 },
  },
  /**
   * Hero surface: this is every face and every pair of hands in the build.
   *
   * WAS a flat salmon ball — `tvor(uv, 260)` pores at 1024 is 3.9 texels per cell, so
   * the pore layer aliased into nothing and the mip chain averaged it flat, and the
   * dermal zoning ran at 14 cm and 3.4 cm over a 55 cm tile, i.e. barely one blotch
   * across a face. NOW 2048 with a 30 cm tile (7x the texel density on a head), pores
   * at a frequency the map can actually hold, three-zone dermal colouring, and the
   * specular broken up by sebum so the highlight is not one clean lobe.
   */
  /**
   * SKIN — measured at 6.0 deg of hue travel across a whole sphere and 4.2 deg across
   * cheek / nose-tip / jaw. Real skin needs 25-40 and a saturation INVERSION at the
   * terminator. Three things changed and all three were needed:
   *
   * 1. The additive stack came off. `sheen` 0.20 + `clearcoat` 0.12 + a broad
   *    forward-scatter `translucent` are three ACHROMATIC-to-salmon terms added on top
   *    of the diffuse, and their combined effect on a curved surface is to raise the
   *    floor everywhere and compress exactly the chroma range being measured. My
   *    predecessor halved the transmission and the tint blend and reported it was not
   *    enough; it was not enough because halving a wash still leaves a wash. Sheen goes
   *    to a narrow, strongly red peach-fuzz rim (which ADDS hue travel at the
   *    silhouette instead of removing it), clearcoat drops to a trace, and the broad
   *    forward-scatter term is retired in favour of (3).
   * 2. The map now carries four dermal zones spanning ~12-52 deg instead of two
   *    spanning 23, mixed at 0.88-0.96 instead of 0.58-0.72, plus a capillary net.
   * 3. `sss` — a per-channel diffusion wrap in the shader. This is the one that
   *    actually produces the saturation inversion, because it is the only term that
   *    knows where the light is. See the SURF_SKIN block in runtime.glsl.js.
   */
  skin: {
    family: 'skin', surface: 'flesh', res: 2048, tile: 0.30, seed: 463,
    /* base / capillary-red / sallow-carotene / stubble.
     *
     * HUE is separated, CHROMA is not. Hue is what the acceptance measures and it is
     * scale-invariant, so the three zones can sit 13 / 26 / 54 degrees apart while all
     * three stay near saturation 0.25 — which is where skin actually lives. An earlier
     * tuning bought the same hue span with a chartreuse 0x9d9e33 at saturation 0.55 and
     * the sphere came out pink with bright yellow blotches: a better number and a worse
     * material, which is the wrong trade to make in front of an art director. */
    /* THE SET THAT SHIPPED WAS NOT CHROMA-MATCHED, AND THAT IS THE WHOLE DEFECT.
     *
     * WAS 0xa07f63 / 0xaf6d59 / 0x968c46 — HSV saturation 0.381 / 0.491 / 0.533 and
     * hue 27.5 / 14.0 / 52.5. The comment above it claimed the three were matched; only
     * their luma was, and only roughly. Two consequences, both visible on
     * shots/char_closeup.png in the iteration-3 build:
     *
     *   1. The hue travel was bought with CHROMA. Wherever the capillary zone won, the
     *      texel came out 30 % more saturated than base, and the capillary zone is the
     *      cheek, nose and brow — i.e. the parts of a face that catch the key. The
     *      most-lit part of the face became the most saturated part of it, which is the
     *      exact inverse of a photograph, and it measured as nose saturation 0.568 with
     *      the cheek clipping at p99 0.98.
     *   2. 0x968c46 is 52.5 degrees at saturation 0.533. It only takes the fine mottle's
     *      +-10 % red / -+9 % blue wobble to push a texel there past G > R, and past
     *      G > R the hue leaves the 0-60 band entirely: green-yellow patches on the jaw
     *      and the neck. There is no such thing as green skin.
     *
     * NOW all three sit at HSV saturation 0.300 and Rec.709 luma 133/255, separated ONLY
     * in hue and only across 11 - 41 degrees, i.e. a 30-degree rotation at constant
     * chroma and constant value. Derived, not picked: for hue h and S = 0.30,
     * L = V*(0.76378 + 0.003576*h), so V follows from the luma target.
     *
     *   0xa67d74   hue 10.8   S 0.301   luma 133.1   capillary bed  (cheek, nose, ear)
     *   0x9c816d   hue 25.5   S 0.301   luma 133.4   base dermis
     *   0x928466   hue 40.9   S 0.301   luma 132.8   sallow / carotene (forehead, jaw)
     *
     * Everything above 41 degrees is deliberately unreachable: it is the approach to
     * green and there is no way to police it once eight multiplicative layers have run
     * over it. The shader also carries a hard R >= G >= B guard for the same reason. */
    colors: [0x9c816d, 0xa67d74, 0x928466, 0x241e18],
    params: [
      [1.0, 0.65, 0.55, 0.55],   // pores, stubble, blemish, sebum
      [1.0, 0.72, 0, 0],         // dermal zoning, capillary flush
    ],
    ns: 1.45, hs: 0.052, aos: 1.0, metal: 0,
    physical: true,
    mat: {
      roughness: 1.0, metalness: 0.0,
      /* peach fuzz: narrow, red, and at the silhouette only.
       * Sheen and clearcoat are ADDITIVE terms on top of a diffuse that is already at
       * the top of the frame's value range on a sunlit face — they are what took the
       * cheek to p99 0.98, i.e. clipping. Halved. The correct currency for "the face is
       * too hot" is the additive stack, not the albedo: dimming the albedo would cost
       * the face its value structure, which is the F15 mistake in another material. */
      sheen: 0.07, sheenRoughness: 0.94, sheenColor: new THREE.Color(0x7a2a18),
      clearcoat: 0.02, clearcoatRoughness: 0.55,
      specularIntensity: 0.55,
    },
    /* detailAlbedo 0.26 -> 0.14: the self-detail layer re-tiles the skin's own normal
     * map at 7x, so on a neck at 1.5 m its albedo modulation lands at ~3 screen pixels
     * and reads as a rash rather than as pores. The relief and the micro-roughness stay
     * at full strength — it is only the albedo copy that was too loud. */
    sh: { detailTile: 7, detailStrength: 0.60, detailRough: 0.16, detailAlbedo: 0.14,
          hex: false, macro: false,
          /* Wrap tightened 0.72 -> 0.52 and strength raised to compensate. A wide wrap
           * reddens the ENTIRE lit hemisphere, which does not add hue travel — it
           * just moves the whole distribution — and it eats the sallow zone. A tight
           * wrap at higher strength keeps the terminator band and leaves the lit side
           * showing the map. */
          /* SSS strength 0.42 -> 0.20, tint saturation 0.63 -> 0.45, shadow floor
           * 0.14 -> 0.07. At the old values the terminator band printed as a hard red
           * STRIPE rather than as reddening — worst under the helmet brim, where the
           * cast-shadow edge kills the diffuse but the shadow floor keeps 14 % of a
           * strongly saturated red flowing, so the RATIO of scattered red to everything
           * else steps discontinuously across the shadow line. That is the "orange-red
           * band across the brow" the reviewer measured, and it is the same failure mode
           * the comment in the SURF_SKIN block warns about, re-entering through the
           * floor instead of through the wrap. */
          sss: 0.20, sssWrap: [0.60, 0.15, 0.06], sssTint: 0xf2a184,
          sssShadowFloor: 0.07, sssCurvature: 0.45, sssAmbient: 0xdceaff,
          /* THE OTHER HALF OF "SATURATION FALLS INTO THE HIGHLIGHT".
           * `sss` reddens the terminator (saturation UP as luminance goes DOWN). This is
           * its counterpart at the other end: where the surface faces the key hard, the
           * light that comes back has spent almost no path length in the dermis, so it
           * carries much less of haemoglobin's absorption and reads closer to the
           * illuminant. Implemented as a luma-preserving desaturation of the direct
           * diffuse, so it can only move chroma — it cannot brighten a pixel and it can
           * never be the reason something clips. See the SURF_SKIN block. */
          sssHotDesat: 0.34,
          translucent: 0.05, transPower: 5.0, transWrap: 0.70,
          transTint: 0xc9402a, transTintMix: 0.55,
          ibl: 0.80 },
  },

  /* ===================================================================== *
   * WEAPON SURFACES — the differentiated set
   *
   * The critique measured nine components of `wpn_closeup` and found 3.6 deg of hue,
   * 0.085 of saturation and 0.12 of luminance across six parts that are, in reality,
   * three unrelated materials: an anodised aluminium receiver, a glass-filled polymer
   * handguard and a hard-anodised optic housing. "Everything is present and nothing is
   * differentiated."
   *
   * The library cannot fix the weapon on its own — `src/weapons/GunMaterials.js` ships
   * its own hero maps and only adopts a normal map from here when it has none of its
   * own — so what this block does is provide a set that is genuinely, measurably
   * separated, ready for the weapons team to reference part by part. Reported to them
   * by name:
   *
   *   gun_metal          hard-anodised aluminium, COOL graphite   base hue ~213 deg
   *   gun_alloy          type-III anodised alloy, OLIVE-grey      base hue ~96 deg
   *   gun_steel_bare     bare/polished steel, roughness 0.06-0.16 base hue ~213 deg
   *   gun_optic          black hard anodise, satin, near-neutral  base hue ~218 deg
   *   gun_polymer        glass-filled nylon, WARM brown-black     base hue ~27 deg
   *   gun_polymer_fde    FDE furniture, warm tan                  base hue ~33 deg
   *   gun_polymer_grey   wolf-grey furniture, cool                base hue ~216 deg
   *   gun_rubber         vulcanised grip rubber, dead matte       base hue ~220 deg
   *
   * A three-material weapon built as (receiver: gun_alloy, handguard: gun_polymer_fde,
   * optic: gun_optic, barrel/BCG/charging handle: gun_steel_bare) spans ~180 deg of
   * base hue and 0.06-0.62 of roughness, against the 3.6 deg / one-finish it has now.
   *
   * They are 512 rather than 1024 because the library copies are only ever seen on a
   * character's slung rifle and on the sphere grid, and eight of them at 1024 would be
   * 135 MB of a 640 MB budget that is already at 592 — and going over the budget
   * silently HALVES the resolution of whatever is generated next.
   * ===================================================================== */

  /** Hard-anodised aluminium, graphite. The cool end of the weapon palette. */
  gun_metal: {
    family: 'brushed', surface: 'metal', res: 1024, tile: 0.30, seed: 491,
    colors: [0x666e78, 0x2b3138],
    params: [
      /* rough base 0.30 -> 0.20 and bluing 0.92 -> 0.50. At 0.92 the parkerising branch
       * overwrote roughness with 0.44-0.64 AND lerped the albedo 92 % of the way to
       * 0x2e3134 — a dark, rough, fully metallic surface, i.e. a surface whose entire
       * appearance is a dim sky convolved to nothing. That is the measured "max
       * luminance 0.801 with no environment reflection at all". */
      [70.0, 0.26, 0.34, 0.30],   // brush freq, rough base, aniso spread, edge wear
      [0.66, 0.60, 0.26, 0.16],   // anodise depth, machining, oil, pits
      [0.20, 0.70, 0.62, 0.0],    // polish (bare-metal band), smudge, finish zoning
    ],
    ns: 0.95, hs: 0.024, aos: 0.85, metal: 1.0,
    physical: true,
    mat: { anisotropy: 0.55, anisotropyRotation: Math.PI * 0.5 },
    sh: { detailTile: 8, detailStrength: 0.55, detailRough: 0.10, hex: false, macro: false,
          specAA: true, ibl: 1.30 },
  },
  /** Type-III anodised 7075 — the olive-grey a mil-spec receiver actually goes. */
  gun_alloy: {
    family: 'brushed', surface: 'metal', res: 512, tile: 0.26, seed: 493,
    /* Measured at hue 42.9 deg, saturation 0.259 on the sphere grid — "that is brass on
     * a rifle". The albedo was authored at 0x5f6b57, HSV saturation 0.187 and hue 96, and
     * on a `metalness = 1` surface the albedo is a FILTER on the environment, not a
     * colour: a 0.19-saturation olive filter over a golden-hour sky lands on gold. The
     * cure for a metal reading as the wrong alloy is to take chroma OUT of the filter,
     * not to fight the sky. 0x646860 is saturation 0.075 — still perceptibly olive
     * against `gun_metal`'s graphite and `gun_polymer_fde`'s tan, which is all the
     * separation the part needs, without the alloy changing. The bluing tone goes with
     * it: 0x2f342b is itself saturation 0.16, and this material lerps 62 % of the way to
     * it, so leaving it olive would have put most of the chroma straight back. */
    colors: [0x63665f, 0x313330],
    params: [
      [46.0, 0.34, 0.26, 0.26],
      [0.62, 0.42, 0.20, 0.12],
      [0.22, 0.80, 0.75, 0.0],
    ],
    ns: 1.05, hs: 0.026, aos: 0.90, metal: 1.0,
    physical: true,
    mat: { anisotropy: 0.30, anisotropyRotation: Math.PI * 0.5 },
    sh: { detailTile: 9, detailStrength: 0.55, detailRough: 0.12, hex: false, macro: false,
          specAA: true, ibl: 1.05 },
  },
  /** Bare steel: barrel flats, bolt carrier, charging handle, sling swivels, crown. */
  gun_steel_bare: {
    family: 'brushed', surface: 'metal', res: 512, tile: 0.22, seed: 495,
    /* Mean (0.742, 0.715, 0.680) at saturation 0.085 with nothing varying across it is
     * nickel plate. The polished band stays — it is the build's one real specular source
     * — but it now sits in a surface with part-scale tonal variation (zoning 0.45 -> 0.88)
     * and visible handling (smudge 0.35 -> 0.62, pits 0.10 -> 0.18). Nothing is dimmed:
     * the zoning is symmetric about the mean, so the highlight it exists to carry is
     * unchanged and only the spread grows. */
    colors: [0xa2a8b0, 0x2a2d31],
    params: [
      [58.0, 0.085, 0.30, 0.46],
      [0.0, 0.70, 0.08, 0.18],
      [0.86, 0.62, 0.88, 0.0],
    ],
    ns: 0.90, hs: 0.018, aos: 0.80, metal: 1.0,
    physical: true,
    mat: { anisotropy: 0.62, anisotropyRotation: Math.PI * 0.5 },
    sh: { detailTile: 8, detailStrength: 0.30, detailRough: 0.08, hex: false, macro: false,
          specAA: true, ibl: 1.55 },
  },
  /** Optic housing — black hard anodise. Satin, tight, and NOT the receiver's finish. */
  gun_optic: {
    family: 'brushed', surface: 'metal', res: 512, tile: 0.20, seed: 497,
    colors: [0x3a3d42, 0x1c1e21],
    params: [
      [110.0, 0.28, 0.14, 0.14],
      [0.80, 0.30, 0.34, 0.06],
      [0.10, 0.50, 0.40, 0.0],
    ],
    ns: 0.85, hs: 0.016, aos: 0.90, metal: 1.0,
    physical: true,
    mat: { anisotropy: 0.22, anisotropyRotation: Math.PI * 0.5 },
    sh: { detailTile: 8, detailStrength: 0.45, detailRough: 0.08, hex: false, macro: false,
          specAA: true, ibl: 1.15 },
  },
  /** Glass-filled nylon, black-brown. Warm — the opposite end from the anodising. */
  gun_polymer: {
    family: 'polymer', surface: 'metal', res: 512, tile: 0.30, seed: 499,
    colors: [0x2a2521],
    params: [
      [1.0, 120.0, 0.58, 0.55],   // stipple amount, stipple freq, rough, checkering
      [0.35, 0.7, 0.62, 0.8],     // ribs, scuff, gloss variance, mould line
      [0.85, 0.0, 0, 0],          // glass fibre fleck, soft-touch
    ],
    ns: 1.30, hs: 0.036, aos: 1.15, metal: 0,
    physical: true,
    /* A clearcoat lobe the metals do not have. A moulded polymer part has a thin
     * release-agent skin over a diffuse body; that second, tighter, ACHROMATIC lobe is
     * a large part of why polymer never reads as metal in a photograph. */
    mat: { clearcoat: 0.16, clearcoatRoughness: 0.55, specularIntensity: 0.70 },
    sh: { detailTile: 10, detailStrength: 0.6, detailRough: 0.12, hex: false, macro: false,
          specAA: true, ibl: 0.72 },
  },
  /** FDE furniture — stock, grip, handguard on a tan-furniture rifle. */
  gun_polymer_fde: {
    family: 'polymer', surface: 'metal', res: 512, tile: 0.30, seed: 501,
    /* Measured saturation 0.421. 0x8a7050 is HSV 0.42 — that is a terracotta plant pot,
     * not Cerakote. Real FDE / Magpul flat dark earth sits around 0.24-0.30: it is a
     * DARK EARTH, and the "flat" in the name is doing work. 0x82705d is 0.286 at the same
     * hue 33, so the part still separates from the graphite receiver without shouting.
     * (First pass at 0x82705d measured 0.354 on the sphere — the warm key adds chroma of
     * its own, so the map has to sit below the target, not on it.) */
    colors: [0x7c6e60],
    params: [
      [1.0, 132.0, 0.62, 0.42],
      [0.30, 0.85, 0.55, 0.7],
      [0.70, 0.0, 0, 0],
    ],
    ns: 1.30, hs: 0.036, aos: 1.15, metal: 0,
    physical: true,
    mat: { clearcoat: 0.10, clearcoatRoughness: 0.62, specularIntensity: 0.62 },
    sh: { detailTile: 10, detailStrength: 0.6, detailRough: 0.12, hex: false, macro: false,
          specAA: true, ibl: 0.68 },
  },
  /** Wolf grey furniture — cool, so a two-tone build still separates in hue. */
  gun_polymer_grey: {
    family: 'polymer', surface: 'metal', res: 512, tile: 0.30, seed: 503,
    colors: [0x5c6066],
    params: [
      [1.0, 126.0, 0.60, 0.48],
      [0.32, 0.75, 0.58, 0.75],
      [0.62, 0.0, 0, 0],
    ],
    ns: 1.28, hs: 0.034, aos: 1.15, metal: 0,
    physical: true,
    mat: { clearcoat: 0.12, clearcoatRoughness: 0.58, specularIntensity: 0.66 },
    sh: { detailTile: 10, detailStrength: 0.6, detailRough: 0.12, hex: false, macro: false,
          specAA: true, ibl: 0.70 },
  },
  /** Grip rubber / buttpad — dead matte, no clearcoat, the flattest thing on the gun. */
  gun_rubber: {
    family: 'polymer', surface: 'metal', res: 512, tile: 0.18, seed: 505,
    colors: [0x1d1e20],
    params: [
      [1.0, 96.0, 0.82, 0.70],
      [0.55, 0.30, 0.22, 0.5],
      [0.0, 1.0, 0, 0],           // soft-touch: roughness floored high, no gloss
    ],
    ns: 1.35, hs: 0.044, aos: 1.25, metal: 0,
    sh: { detailTile: 12, detailStrength: 0.6, detailRough: 0.14, hex: false, macro: false,
          specAA: true, ibl: 0.45 },
  },
};

/** friendly aliases for names other teams have already typed */
const ALIASES = {
  crate_wood: 'wood_crate',
  wood_box: 'wood_crate',
  concrete: 'concrete_wall',
  metal: 'metal_painted',
  wood: 'wood_plank',
  steel: 'steel_brushed',
  glassPane: 'glass',
  // bare metal, by the names other teams are likely to reach for
  bare_metal: 'metal_bare',
  steel_bare: 'metal_bare',
  chrome: 'metal_bare',
  // weapon surfaces, by the part they usually clothe
  gun_receiver: 'gun_alloy',
  gun_barrel: 'gun_steel_bare',
  gun_bolt: 'gun_steel_bare',
  gun_optic_body: 'gun_optic',
  gun_grip: 'gun_rubber',
  gun_furniture: 'gun_polymer_fde',
};

const NAMES = Object.keys(DEFS);

/* ===================================================================== */

export function createMaterialSystem() {
  let factory = null;
  let ctxRef = null;
  const cache = new Map();          // materialKey -> THREE.Material
  const mapCache = new Map();       // name -> {albedo, orm, normal}
  const live = new Set();           // every material this library has handed out
  let detailTier = 'full';
  const fullShadingOpts = new WeakMap(); // material -> authored shading opts, for tier restore
  const warned = new Set();
  let envMap = null;
  let showcase = null;

  const resolveName = (name) => (DEFS[name] ? name : (ALIASES[name] || null));

  function mapsFor(name) {
    let m = mapCache.get(name);
    if (m) return m;
    const def = DEFS[name];
    m = factory.generate(name, {
      family: def.family,
      res: def.res,
      seed: def.seed ?? 1,
      params: def.params,
      colors: def.colors,
      normalStrength: def.ns ?? 1,
      aoStrength: def.aos ?? 1,
      heightScale: def.hs ?? 0.05,
      heightContrast: def.hc ?? 4.0,
    });
    mapCache.set(name, m);
    return m;
  }

  function buildMaterial(name, opts = {}) {
    const def = DEFS[name];
    const maps = mapsFor(name);

    const Ctor = def.physical ? THREE.MeshPhysicalMaterial : THREE.MeshStandardMaterial;
    const mat = new Ctor({
      name,
      color: 0xffffff,
      roughness: 1.0,
      metalness: def.metal ?? 0.0,
      ...(def.mat || {}),
    });

    // Only three samplers are bound. metalnessMap / aoMap are deliberately NOT set:
    // the injected shading reads AO and metalness out of the packed ORM texture that is
    // already bound as `roughnessMap`, and binding the same texture three more times
    // costs three texture image units. On this target the fragment stage only has 16,
    // shared with the env map, the detail map and every shadow map in the scene.
    mat.map = maps.albedo;
    mat.normalMap = maps.normal;
    mat.roughnessMap = maps.orm;
    mat.normalScale.set(def.normalScale ?? 1, def.normalScale ?? 1);
    mat.envMapIntensity = def.mat?.envMapIntensity ?? 1.0;

    // roughness is a multiplier over the ORM map's green channel.
    if (def.mat?.roughness === undefined) mat.roughness = 1.0;
    /* metalness is NOT a multiplier. The injected shader reads it from the ORM map's
     * blue channel; this scalar is the physically-authored value of the surface, and
     * it is what a raw `.clone()` (Env.js probe materials, anything outside make())
     * would fall back on if the injection were lost. Leaving every material on 1.0
     * "because the map scales it" is what made `prop:palm_tree:wood_plank` report —
     * and, once cloned, render — as bare metal. */
    if (def.mat?.metalness === undefined) mat.metalness = def.metal ?? 0.0;

    // `worldSize` is how many metres the mesh's 0..1 UV span covers. Pass a number for
    // a square, or [w, h] to keep the texel density square on a non-square surface.
    const ws = opts.worldSize;
    let rep = opts.repeat;
    if (!rep && ws !== undefined) {
      const t = def.tile;
      rep = Array.isArray(ws) ? [ws[0] / t, ws[1] / t] : [ws / t, ws / t];
    }
    if (!rep) rep = [1, 1];

    const sh = {
      ...(def.sh || {}),
      repeat: rep,
      offset: opts.offset,
      triplanar: opts.triplanar ?? def.sh?.triplanar ?? false,
      triplanarScale: 1 / def.tile,
      macro: def.sh?.macro ?? false,
      hex: opts.hex ?? def.sh?.hex ?? false,
      pom: (opts.pom ?? def.sh?.pom) || 0,
      translucent: def.sh?.translucent ?? 0,
      /* Callers may switch the wet/polished response on for any material without a new
       * texture — it is a runtime shading term driven off world position and the
       * material's own baked cavity map, so it costs no sampler and no memory.
       *   ctx.materials.get('concrete_floor', { wet: 0.8 })   // a wet loading bay
       * See CONTRACTS / FINDINGS F13: bare metal and wet ground are the two content
       * sources of specular this build has never had. */
      wet: opts.wet ?? def.sh?.wet ?? 0,
      sss: def.sh?.sss ?? 0,
      ibl: opts.ibl ?? def.sh?.ibl ?? 1.0,
    };
    if (opts.detailTile) sh.detailTile = opts.detailTile;
    if (opts.wetRough !== undefined) sh.wetRough = opts.wetRough;

    applySurfaceShading(mat, sh);

    mat.userData.surface = def.surface;
    mat.userData.materialName = name;
    mat.userData.tile = def.tile;
    if (envMap && !mat.envMap) mat.envMap = null; // scene.environment is used instead

    live.add(mat);
    return mat;
  }

  function keyFor(name, opts) {
    if (!opts) return name;
    return name + '|' + JSON.stringify(opts);
  }

  function fallback() {
    let m = cache.get('__fallback');
    if (!m) {
      m = new THREE.MeshStandardMaterial({ color: 0x8a8a8a, roughness: 0.9, metalness: 0.0, name: 'fallback' });
      m.userData.surface = 'concrete';
      cache.set('__fallback', m);
    }
    return m;
  }

  const lib = {
    get names() { return NAMES.slice(); },
    get blueNoise() { return factory ? factory.getBlueNoise() : null; },
    get detailNormal() { return factory ? factory.getDetailNormal() : null; },
    get envMap() { return envMap; },

    get(name, opts) {
      const n = resolveName(name);
      if (!n) {
        if (!warned.has(name)) {
          warned.add(name);
          console.warn(`[materials] unknown material "${name}" — using fallback. Known: ${NAMES.join(', ')}`);
        }
        return fallback();
      }
      const k = keyFor(n, opts);
      let m = cache.get(k);
      if (!m) { m = buildMaterial(n, opts); cache.set(k, m); }
      return m;
    },

    make(name, opts) {
      const base = lib.get(name, opts);
      /* `applySurfaceShading` installs an instance-level `clone()` that re-applies the
       * injection with the same options table, so this is now correct for every caller
       * — including Env.js's probe clones, which this module never sees. The options
       * are kept verbatim in that side table; reverse-engineering them back out of the
       * uniform values silently dropped every flag that has no uniform of its own. */
      const c = base.clone();
      const n = resolveName(name);
      if (n) {
        const def = DEFS[n];
        c.userData.surface = def.surface;
        c.userData.materialName = n;
        c.userData.tile = def.tile;
      }
      live.add(c);
      return c;
    },

    texture(name, opts) {
      const n = resolveName(name);
      if (!n) return null;
      const maps = mapsFor(n);
      const t = maps.albedo;
      t.maps = { albedo: maps.albedo, orm: maps.orm, normal: maps.normal, roughness: maps.orm, ao: maps.orm };
      return t;
    },

    tileMetres(name) {
      const n = resolveName(name);
      return n ? DEFS[n].tile : 1;
    },
    surfaceOf(name) {
      const n = resolveName(name);
      return n ? DEFS[n].surface : 'concrete';
    },
    stats() { return factory ? factory.stats() : null; },

    /**
     * Debug/ablation hooks. `tune` walks every material this library has handed out
     * (including `make()` clones, which is what the level actually uses) and hands the
     * caller its injected uniform block, so a shading term can be switched off from a
     * console snippet and proved to be — or not to be — the cause of an artefact.
     * FINDINGS: "an ablation is only evidence if you have verified it took effect".
     *   ctx.materials.tune((m, u) => { u.uMiscP.value.y = 1; })   // no aniso widening
     */
    shading(mat) { return getSurfaceShading(mat); },

    /**
     * Surface detail tier. 'lite' strips the per-fragment detail machinery
     * (POM, hex tiling, octave, macro, façade, wet, edge wear, spec AA, detail
     * layer) and recompiles every live material — measured as the bulk of the
     * remaining frame on integrated GPUs once the practical-light count is
     * down. 'full' restores the authored look. Switching re-links ~90 programs,
     * so callers should treat it like a preset change, not a per-frame knob.
     */
    setDetailTier(tier) {
      if (tier === detailTier) return 0;
      detailTier = tier;
      let n = 0;
      for (const m of live) {
        const st = getSurfaceShading(m);
        if (!st) continue;
        if (!fullShadingOpts.has(m)) fullShadingOpts.set(m, st.opts);
        const o = fullShadingOpts.get(m);
        const next = tier === 'lite'
          ? {
              ...o, pom: false, hex: false, octave: false, macro: false,
              facade: false, wet: 0, edgeWear: 0, uvJitter: 0,
              specAA: false, detail: false,
            }
          : o;
        applySurfaceShading(m, next);
        reRegisterMaterial(m);
        n++;
      }
      return n;
    },

    tune(fn) {
      let n = 0;
      for (const m of live) {
        const st = getSurfaceShading(m);
        if (st) { fn(m, st.uniforms, st.opts); n++; }
      }
      return n;
    },
    /** Every material name paired with the metalness it is authored at. */
    metalnessAudit() {
      return NAMES.map((n) => [n, DEFS[n].mat?.metalness ?? DEFS[n].metal ?? 0]);
    },

    /** Free the half-float scratch buffers once the level has stopped requesting maps. */
    finalize() { if (factory) factory.releaseScratch(); },

    dispose() {
      for (const m of cache.values()) m.dispose();
      cache.clear();
      mapCache.clear();
      if (factory) factory.dispose();
    },
  };

  /* ------------------------------------------------------------------ *
   * fallback environment — replaced by the lighting team if they set one
   * ------------------------------------------------------------------ */
  function buildEnv(renderer) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const scene = new THREE.Scene();
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(8, 32, 16),
      new THREE.ShaderMaterial({
        side: THREE.BackSide,
        depthWrite: false,
        uniforms: { uSun: { value: new THREE.Vector3(0.4, 0.65, 0.35).normalize() } },
        vertexShader: `
          varying vec3 vDir;
          void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
        fragmentShader: `
          varying vec3 vDir;
          uniform vec3 uSun;
          void main(){
            vec3 d = normalize(vDir);
            float up = clamp(d.y * 0.5 + 0.5, 0.0, 1.0);
            vec3 zenith  = vec3(0.10, 0.17, 0.31);
            vec3 horizon = vec3(0.46, 0.50, 0.55);
            vec3 ground  = vec3(0.10, 0.088, 0.072);
            vec3 c = mix(horizon, zenith, pow(clamp(d.y, 0.0, 1.0), 0.55));
            c = mix(ground, c, smoothstep(-0.06, 0.06, d.y));
            float sd = max(dot(d, normalize(uSun)), 0.0);
            c += vec3(1.0, 0.82, 0.60) * pow(sd, 900.0) * 22.0;
            c += vec3(1.0, 0.78, 0.55) * pow(sd, 8.0) * 0.30;
            gl_FragColor = vec4(c, 1.0);
          }`,
      }),
    );
    scene.add(sky);
    const rt = pmrem.fromScene(scene, 0.02);
    sky.geometry.dispose();
    sky.material.dispose();
    pmrem.dispose();
    return rt.texture;
  }

  /* ------------------------------------------------------------------ *
   * Showcase scene (debug / capture only)
   * ------------------------------------------------------------------ */
  /* Inside the level's own volume. The render pipeline's aerial perspective / volumetric
   * fog is a world-space froxel grid fitted to the playable area — anything outside it
   * (a kilometre away, or 40 m underground) clamps to the densest slice and is swallowed
   * by haze. So the stage sits where the fog is calibrated and the level is hidden for
   * the duration of the shot; `update()` puts it straight back the moment another team's
   * shot moves the camera, so nothing leaks between shots in a full capture run. */
  const SHOW_ORIGIN = new THREE.Vector3(0, 0, 0);
  let shotCam = null;
  let hiddenLevel = null;

  /* my showcase lights, with the intensity they were tuned at against a 3.2 sun */
  const myLights = [];
  const ownLight = (l, base) => { myLights.push([l, base]); l.intensity = base; return l; };

  /**
   * The lighting team works in calibrated photometric units, so a fixed intensity here
   * would either vanish or blow out depending on their exposure. Scale the whole rig by
   * the level's own key light instead.
   */
  function matchExposure(ctx) {
    const mine = new Set(myLights.map((e) => e[0]));
    let keyI = 0;      // the level's shadow-casting key — the light the exposure is built around
    let anyI = 0;
    ctx.scene.traverse((o) => {
      if (!o.isDirectionalLight || mine.has(o) || !o.visible) return;
      anyI = Math.max(anyI, o.intensity);
      if (o.castShadow) keyI = Math.max(keyI, o.intensity);
    });
    // NB: the level also carries a very high-intensity sun used by the sky/atmosphere
    // model. Keying off the maximum blows the stage out by ~25x, so prefer the
    // shadow-casting key light and clamp hard either way.
    let ref = keyI > 0 ? keyI : anyI;
    if (!(ref > 0)) ref = 3.2;
    const k = Math.min(3, Math.max(0.4, ref / 3.2));
    for (const [l, base] of myLights) l.intensity = base * k;
    return k;
  }

  /**
   * ONE atlas and ONE mesh for all 41 name plates.
   *
   * WAS one 512x96 CanvasTexture and one PlaneGeometry per material. That is 41 textures
   * and 41 draw calls whose only job is to say what you are looking at, and it was 41 of
   * the 79 textures `mat_sphere_grid` allocated on its own shot — over half of the leak
   * the harness flagged. A label sheet is the one thing in this build that is genuinely
   * an atlas: same material, same shader, differing only in UV.
   *
   * @returns {{ texture: THREE.CanvasTexture, uvFor(i): number[] }}
   */
  const LABEL_COLS = 4, LABEL_CW = 512, LABEL_CH = 128;
  function buildLabelAtlas(names) {
    const rows = Math.ceil(names.length / LABEL_COLS);
    const c = document.createElement('canvas');
    c.width = LABEL_COLS * LABEL_CW;
    c.height = rows * LABEL_CH;
    const g = c.getContext('2d');
    g.fillStyle = '#000000';
    g.fillRect(0, 0, c.width, c.height);
    g.fillStyle = '#ffffff';
    g.font = 'bold 54px Helvetica, Arial, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    names.forEach((n, i) => {
      const col = i % LABEL_COLS, row = (i / LABEL_COLS) | 0;
      g.fillText(n, (col + 0.5) * LABEL_CW, (row + 0.5) * LABEL_CH, LABEL_CW - 24);
    });
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 4;
    t.generateMipmaps = true;
    return {
      texture: t,
      rows,
      /** [u0, v0, u1, v1] for label i, in three's bottom-up UV convention. */
      uvFor(i) {
        const col = i % LABEL_COLS, row = (i / LABEL_COLS) | 0;
        const u0 = col / LABEL_COLS, u1 = (col + 1) / LABEL_COLS;
        const v1 = 1 - row / rows, v0 = 1 - (row + 1) / rows;
        return [u0, v0, u1, v1];
      },
    };
  }

  function buildShowcase(ctx) {
    if (showcase) return showcase;
    const root = new THREE.Group();
    root.name = 'materials:showcase';
    root.position.copy(SHOW_ORIGIN);
    root.visible = false;
    ctx.scene.add(root);

    /* --- sphere grid --- */
    const grid = new THREE.Group();
    grid.name = 'mat_grid';
    root.add(grid);
    const sphereGeo = new THREE.SphereGeometry(0.46, 96, 64);
    // 41 materials now, so the sheet is six rows deep instead of five. The row pitch
    // and the camera come in together — a grid that runs off the bottom of the frame is
    // a material sheet that silently stops being evidence for the last row.
    const cols = 7;
    const sx = 1.25, sy = 1.30;
    const gridTop = 3.30;
    /* Lights live INSIDE the showcase groups. three skips invisible objects when it
     * collects lights, so nothing here touches the real level — the groups are only
     * visible while one of my shots is being captured. */
    const key = ownLight(new THREE.DirectionalLight(0xfff2e2, 1), 2.6);
    key.position.set(-6, 7, 9);
    key.target.position.set(0, 0.4, 0);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    {
      const c = key.shadow.camera;
      c.left = -7; c.right = 7; c.top = 6; c.bottom = -6; c.near = 1; c.far = 30;
      c.updateProjectionMatrix();
    }
    key.shadow.bias = -0.0008;
    key.shadow.normalBias = 0.01;
    key.shadow.radius = 1.2;
    grid.add(key, key.target);
    grid.userData.key = key;

    const fill = ownLight(new THREE.PointLight(0x8fb4dc, 1, 40, 2), 46);
    fill.position.set(8, 3.0, 8);
    grid.add(fill);

    const rim = ownLight(new THREE.PointLight(0xffcf9a, 1, 40, 2), 60);
    rim.position.set(3.5, 2.0, -7);
    grid.add(rim);

    const atlas = buildLabelAtlas(NAMES);
    const LW = 1.12, LH = 0.21;
    const pos = [], uvs = [], idx = [];
    NAMES.forEach((n, i) => {
      const cx = (i % cols - (cols - 1) / 2) * sx;
      const cy = -Math.floor(i / cols) * sy;
      const m = lib.get(n, { repeat: [2, 1] });
      const mesh = new THREE.Mesh(sphereGeo, m);
      mesh.position.set(cx, cy + gridTop, 0);
      mesh.castShadow = mesh.receiveShadow = true;
      mesh.userData.surface = m.userData.surface;
      grid.add(mesh);

      // one quad in the shared label mesh, positioned in the grid's own space
      const ly = cy + gridTop - 0.58;
      const x0 = cx - LW / 2, x1 = cx + LW / 2, y0 = ly - LH / 2, y1 = ly + LH / 2;
      const [u0, v0, u1, v1] = atlas.uvFor(i);
      const b = pos.length / 3;
      pos.push(x0, y0, 0.05, x1, y0, 0.05, x1, y1, 0.05, x0, y1, 0.05);
      uvs.push(u0, v0, u1, v0, u1, v1, u0, v1);
      idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    });
    {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      g.setIndex(idx);
      g.computeBoundingSphere();
      const labels = new THREE.Mesh(g, new THREE.MeshBasicMaterial({
        map: atlas.texture, toneMapped: false, transparent: false,
      }));
      labels.name = 'mat_grid_labels';
      grid.add(labels);
      grid.userData.labels = labels;
    }
    /* a neutral backdrop so the silhouettes read */
    const back = new THREE.Mesh(
      new THREE.PlaneGeometry(24, 16),
      new THREE.MeshStandardMaterial({ color: 0x2c2f33, roughness: 0.95, metalness: 0 }),
    );
    back.position.set(0, -0.6, -2.2);
    back.receiveShadow = true;
    grid.add(back);

    /* --- closeup stages --- */
    const closeups = new THREE.Group();
    closeups.name = 'mat_closeups';
    closeups.position.set(0, 0, 0);
    root.add(closeups);

    /**
     * A closeup stage is a large vertical panel of one material with the camera 32 cm
     * off it at a grazing angle.
     *
     * The panel deliberately faces AWAY from the level's directional sun (which my
     * shots cannot switch off and which casts no shadow out here), so the only key is
     * the stage's own raking light. That is what makes POM silhouettes, detail normals
     * and micro-roughness legible instead of being flattened by a fixed ambient wash.
     */
    const stageFor = (name, idx) => {
      const g = new THREE.Group();
      g.name = `mat_stage_${idx}`;
      g.userData.stageIndex = idx;
      /* Big enough that the panel and its floor fill the frame at 32 cm. The old
       * 11 x 3.6 m stage left sky and open desert in the left third of every
       * closeup, so a third of the "material evidence" was background. */
      const PW = 26, PH = 9;

      // panel normal = -Z, i.e. pointing away from the sun direction (+X +Y +Z)
      const panelGeo = new THREE.PlaneGeometry(PW, PH, 1, 1);
      panelGeo.rotateY(Math.PI);
      panelGeo.translate(0, PH / 2, 0);
      const panelMat = lib.make(name, { worldSize: [PW, PH] });
      const panel = new THREE.Mesh(panelGeo, panelMat);
      panel.receiveShadow = true;
      panel.userData.surface = panelMat.userData.surface;
      g.add(panel);

      // ground in front, same material, for context and a second grazing angle
      const floorGeo = new THREE.PlaneGeometry(PW, 12, 1, 1);
      floorGeo.rotateX(-Math.PI / 2);
      floorGeo.translate(0, 0, -6);
      const floorMat = lib.make(name, { worldSize: [PW, 12] });
      const floor = new THREE.Mesh(floorGeo, floorMat);
      floor.receiveShadow = true;
      floor.userData.surface = floorMat.userData.surface;
      g.add(floor);

      // a bracket bolted to the panel — a hard cast shadow is the honest depth cue
      const bracket = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.05, 0.34),
        new THREE.MeshStandardMaterial({ color: 0x0d0d0e, roughness: 0.7, metalness: 0.6 }),
      );
      bracket.position.set(-0.35, 1.42, -0.17);
      bracket.rotation.x = 0.12;
      bracket.castShadow = true;
      g.add(bracket);

      closeups.add(g);
      return g;
    };

    /* ONE raking key shared by all four stages, moved to whichever stage is being shot.
     * Every extra shadow-casting light is a fragment texture image unit, and the level
     * already spends most of the 16 on its own shadow maps. */
    const rake = ownLight(new THREE.DirectionalLight(0xfff1e0, 1), 16.0);
    rake.position.set(-4.4, 2.4, -1.05);
    rake.target.position.set(2.2, 0.9, 0.0);
    rake.castShadow = true;
    rake.shadow.mapSize.set(2048, 2048);
    {
      // tight frustum: VSM bleeds thin shadows away when the occluder-to-receiver
      // distance is a small fraction of the depth range
      const c = rake.shadow.camera;
      c.left = -8; c.right = 8; c.top = 4.5; c.bottom = -4.5; c.near = 0.05; c.far = 18;
      c.updateProjectionMatrix();
    }
    rake.shadow.bias = -0.0006;
    rake.shadow.normalBias = 0.004;
    rake.shadow.radius = 0.7;
    rake.shadow.blurSamples = 8;
    closeups.add(rake, rake.target);

    const closeFill = ownLight(new THREE.PointLight(0x8fb0d8, 1, 12, 2), 2.2);
    closeFill.position.set(0.2, 1.6, -1.6);
    closeups.add(closeFill);
    closeups.userData.rake = rake;
    closeups.userData.fill = closeFill;

    const closeNames = ['concrete_wall', 'metal_rusted', 'wood_plank', 'canvas'];
    closeNames.forEach((n, i) => stageFor(n, i));

    /* --- tiling test: a 40 m corridor viewed down its length --- */
    const tiling = new THREE.Group();
    tiling.name = 'mat_tiling';
    tiling.position.set(0, 0, 0);
    root.add(tiling);
    {
      const L = 40, W = 9, H = 3.4;
      const floorGeo = new THREE.PlaneGeometry(W, L, 1, 1);
      floorGeo.rotateX(-Math.PI / 2);
      floorGeo.translate(0, 0, L / 2);
      const floor = new THREE.Mesh(floorGeo, lib.get('concrete_floor'));
      floor.receiveShadow = true;
      tiling.add(floor);

      const mkWall = (sign, matName) => {
        const gm = new THREE.PlaneGeometry(L, H, 1, 1);
        gm.rotateY(sign * Math.PI / 2);
        gm.translate(sign * -W / 2, H / 2, L / 2);
        const m = lib.get(matName, { worldSize: [L, H] });
        const mesh = new THREE.Mesh(gm, m);
        mesh.receiveShadow = true;
        return mesh;
      };
      tiling.add(mkWall(1, 'brick_wall'));
      tiling.add(mkWall(-1, 'concrete_wall'));

      const ceil = new THREE.PlaneGeometry(W, L, 1, 1);
      ceil.rotateX(Math.PI / 2);
      ceil.translate(0, H, L / 2);
      const ceilMesh = new THREE.Mesh(ceil, lib.get('concrete_wall_painted', { worldSize: [W, L] }));
      tiling.add(ceilMesh);

      /* sun raking in from the open end, plus dim strip lights down the corridor */
      const sunL = ownLight(new THREE.DirectionalLight(0xffe9cd, 1), 1.1);
      sunL.position.set(-9, 9, 34);
      sunL.target.position.set(1, 0, 10);
      sunL.castShadow = true;
      sunL.shadow.mapSize.set(2048, 2048);
      {
        const c = sunL.shadow.camera;
        c.left = -14; c.right = 14; c.top = 14; c.bottom = -14; c.near = 1; c.far = 60;
        c.updateProjectionMatrix();
      }
      sunL.shadow.bias = -0.0009;
      sunL.shadow.normalBias = 0.02;
      tiling.add(sunL, sunL.target);
      tiling.userData.sun = sunL;

      for (let i = 0; i < 6; i++) {
        const l = ownLight(new THREE.PointLight(0xffe0b4, 1, 14, 2), 1.6);
        l.position.set(0, H - 0.5, 4 + i * 7);
        tiling.add(l);
      }
    }

    showcase = { root, grid, closeups, tiling, closeNames };
    return showcase;
  }

  function show(ctx, which) {
    const sc = buildShowcase(ctx);
    /* THE BETWEEN-SHOT RESET HIDES EVERY DESCENDANT, NOT JUST THE ROOT.
     *
     * `main.js :: restoreVisibility()` now walks the whole scene and hides anything
     * added since boot — "character spawns, FX stages, probe rigs, material showcases"
     * — one object at a time:
     *     root.traverse(o => { if (!snapshot.has(o) && o.visible) o.visible = false; })
     * Re-showing the four top-level groups therefore restores nothing: every sphere,
     * label, panel, floor, bracket and stage light inside them has been individually
     * switched off. The symptom is precise and it is exactly the old C2 defect wearing
     * a new cause — the FIRST of my shots in a run renders (the showcase does not exist
     * yet when the reset runs, so nothing is hidden) and every one after it is an empty
     * ground-and-sky frame. `mat_closeup_*` and `mat_tiling_test` were blank in the
     * 03:29 run and correct when captured alone, which is that signature.
     *
     * A showcase is transient staging by definition, so the fix belongs here rather
     * than in the harness: assert the whole subtree visible, then let the caller switch
     * groups and stages off again below. */
    sc.root.traverse((o) => { o.visible = true; });
    sc.root.visible = true;
    sc.grid.visible = which === 'grid';
    sc.closeups.visible = which === 'closeup';
    sc.tiling.visible = which === 'tiling';

    /* ONLY THE ACTIVE STAGE'S KEY MAY CAST A SHADOW, and `visible` is not enough to
     * enforce that. This is the 116 GL errors, root-caused rather than guessed:
     *
     *   `__GAME__.subjectCoverage('materials')` (the new content gate, spine's file)
     *   collects every object whose name matches /showcase|mat[_-]?stage|.../ — which
     *   picks up `materials:showcase`, the ROOT — and then does
     *   `r.traverse(o => o.visible = true)` before rendering the subject alone offscreen.
     *   That un-hides `mat_closeups` and `mat_tiling` wholesale, and with them the two
     *   idle stage keys, whose `shadow.map` is null because `renderer.shadowMap
     *   .autoUpdate` is false and no refresh has ever run with them on screen. three then
     *   binds its 1x1 `emptyShadowTexture` into `sampler2DShadow directionalShadowMap[]`
     *   — and the ARRAY uniform path never sets that texture's compareFunction, so ANGLE
     *   sees a non-comparison depth texture in a shadow sampler, reports
     *   "Mismatch between texture format and sampler type" and DISCARDS the draw.
     *   Measured: 116 errors, every one of them inside that one coverage render, all 116
     *   draws of the showcase in the 320x180 target.
     *
     * Reproduce (before this fix): boot, setShot('mat_sphere_grid'), step, then call
     * subjectCoverage('materials') and count console GL errors — 0 before the call, 116
     * after it.
     *
     * `castShadow` is the right lever precisely because nothing else in the build
     * touches it: a gate that walks the scene setting `visible` cannot resurrect a
     * caster that has switched itself off. */
    sc.grid.userData.key.castShadow = which === 'grid';
    sc.closeups.userData.rake.castShadow = which === 'closeup';
    sc.tiling.userData.sun.castShadow = which === 'tiling';
    if (ctx.level?.root && ctx.level.root.visible) {
      hiddenLevel = ctx.level.root;
      hiddenLevel.visible = false;
    }
    matchExposure(ctx);

    /* The lighting team runs its own cascades and therefore parks three.js at
     * `renderer.shadowMap.autoUpdate = false`, refreshing once at boot. My stage
     * lights are created lazily, long after that refresh, so their `shadow.map`
     * stays null — and three then binds its 1x1 RGBA placeholder into the
     * `sampler2DShadow directionalShadowMap[]` slot. ANGLE calls that
     * "Mismatch between texture format and sampler type" and DISCARDS the draw,
     * which is why this showcase rendered 33 labels and zero spheres. Ask for one
     * shadow refresh whenever a stage is brought on screen. */
    if (ctx.renderer && ctx.renderer.shadowMap) ctx.renderer.shadowMap.needsUpdate = true;
    return sc;
  }

  /**
   * Point the camera and, crucially, take the player rig with it. The player system
   * owns ctx.camera every frame, so a shot that only calls look() gets overwritten on
   * the very next step. Mirrors what the level team's stage() does.
   */
  const _stageDir = new THREE.Vector3();
  function stage(c, from, at, fov) {
    look(c, from, at, fov);
    shotCam = c.camera.position.clone();
    const sys = c.get?.('player');
    const rig = sys && sys.rig;
    if (rig) {
      sys.freeze?.(true);
      _stageDir.set(at[0] - from[0], at[1] - from[1], at[2] - from[2]).normalize();
      rig.reset?.(Math.atan2(-_stageDir.x, -_stageDir.z),
        Math.asin(THREE.MathUtils.clamp(_stageDir.y, -1, 1)));
      if (rig.eye) rig.eye.copy(c.camera.position);
      if (rig.quaternion) rig.quaternion.copy(c.camera.quaternion);
      rig.fov = fov;
      rig.applyTo?.(c);
    }
    if (c.player) {
      c.player.position.copy(c.camera.position);
      c.player.ads = 0;
    }
    // the weapon viewmodel would sit in the middle of every material shot
    const vm = c.weapons?.viewmodelRoot;
    if (vm) vm.visible = false;
  }

  function registerShots(ctx) {
    registerShot('mat_sphere_grid', (c) => {
      show(c, 'grid');
      const o = SHOW_ORIGIN;
      stage(c, [o.x, o.y - 0.05, o.z + 11.9], [o.x, o.y - 0.05, o.z], 44);
    }, { settle: 10, desc: 'every material on a sphere, labelled' });

    const closeShots = [
      ['mat_closeup_concrete', 0],
      ['mat_closeup_metal', 1],
      ['mat_closeup_wood', 2],
      ['mat_closeup_fabric', 3],
    ];
    for (const [shotName, idx] of closeShots) {
      registerShot(shotName, (c) => {
        const sc = show(c, 'closeup');
        const o = SHOW_ORIGIN;
        // all four stages occupy the same spot; only the one being shot is visible
        for (const g of sc.closeups.children) {
          if (g.isGroup) g.visible = (g.userData.stageIndex === idx);
        }
        /* Eye 34 cm off the panel, sweeping along it — the honest test for POM and
         * detail normals. The look-at is chosen so the *whole* frustum still lands
         * on the wall: at the old azimuth the left edge of the frame pointed back
         * off the panel and a tenth of every material closeup was empty sky. */
        stage(c, [o.x - 1.75, o.y + 1.52, o.z - 0.34], [o.x + 0.85, o.y + 1.16, o.z + 1.95], 42);
      }, { settle: 10, desc: '32 cm grazing closeup of a large panel' });
    }

    registerShot('mat_tiling_test', (c) => {
      show(c, 'tiling');
      const o = SHOW_ORIGIN;
      stage(c, [o.x, o.y + 1.62, o.z + 0.6], [o.x, o.y + 1.15, o.z + 38], 70);
    }, { settle: 10, desc: '40 m corridor down its length — repetition check' });
  }

  return {
    name: 'materials',
    priority: 10,

    init(ctx) {
      ctxRef = ctx;
      factory = new TextureFactory(ctx.renderer, {
        // The plaza deck is the most-looked-at surface in the build and now runs
        // at 2048 so its 0.5-2 cm speckle survives minification at gameplay
        // distance. That took the set to 522 MB, i.e. one material past the old
        // ceiling — and going over silently HALVES the resolution of whatever is
        // generated next, which is a worse failure than the extra memory.
        budgetMB: 640,
        quality: ctx.quality,
      });

      ctx.materials = lib;

      // shared resources other teams asked for
      factory.getDetailNormal(1024);
      factory.getBlueNoise(512);

      // fallback IBL. lighting owns scene.environment; we only fill a hole.
      try {
        envMap = buildEnv(ctx.renderer);
        if (!ctx.scene.environment) ctx.scene.environment = envMap;
      } catch (err) {
        console.warn('[materials] env fallback failed', err);
      }

      registerShots(ctx);

      /* THE SHOWCASE'S TEXTURE SET IS NOT A LEAK, BUT IT IS INDISTINGUISHABLE FROM ONE.
       *
       * `mat_sphere_grid` puts all 41 library materials on screen; roughly a dozen of
       * them (the eight gun_*, tile_terracotta, plastic_crate, metal_bare ...) are used
       * by nothing else in the map, so their map sets were synthesised lazily during that
       * one shot. `capture.mjs` measures textures against a running high-water mark and
       * therefore reported +80 on that shot and called it a leak — correctly, by its own
       * rule, since it cannot tell first-use allocation from a real leak and the last
       * real one (the PMREM render targets, 209 textures) hid behind exactly that excuse.
       *
       * The honest fix is to stop the allocation from landing on a shot: generate the
       * full set at init so it is part of the boot baseline. Gated on `config.debug`
       * because that is precisely when the showcase can be shown at all — a shipped
       * gameplay build has no reason to hold a sphere-grid preview of a rifle finish in
       * VRAM. DEFS order puts the level's own materials first, so if the 640 MB budget
       * does bind it is the previews that get halved, not the map.
       */
      if (ctx.config?.debug) {
        for (const n of NAMES) {
          try { lib.get(n, { repeat: [2, 1] }); } catch (err) { console.warn('[materials] prewarm', n, err); }
        }
      }
    },

    /** Drop the half-float synthesis scratch buffers once the level stops asking for maps. */
    update(dt, ctx) {
      // hand the scene back as soon as the camera leaves one of my shots
      if (shotCam && ctx.camera.position.distanceToSquared(shotCam) > 0.25) {
        shotCam = null;
        if (showcase) showcase.root.visible = false;
        if (hiddenLevel) { hiddenLevel.visible = true; hiddenLevel = null; }
      }
      if (!factory || factory._scratch.size === 0) return;
      const n = mapCache.size;
      if (n !== this._lastCount) { this._lastCount = n; this._idle = 0; return; }
      this._idle = (this._idle || 0) + dt;
      if (this._idle > 1.5) factory.releaseScratch();
      void ctx;
    },

    dispose() {
      if (hiddenLevel) { hiddenLevel.visible = true; hiddenLevel = null; }
      if (showcase) showcase.root.removeFromParent();
      lib.dispose();
      if (envMap) envMap.dispose();
      void ctxRef;
    },
  };
}
